/**
 * The admin console.
 *
 * Platform-wide, across every merchant — which is exactly why it is a separate
 * route tree behind an `isAdmin` gate rather than extra tiles on the merchant
 * dashboard. Every endpoint here is guarded by `requireOperator` server-side;
 * the route guard in the client is a convenience, not the permission.
 */

import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, health } from '../../lib/api.js'
import { ago, dateTime, humanise, initials, money, moneyExact, percent } from '../../lib/format.js'
import {
  Badge,
  Card,
  Drawer,
  DrawerSection,
  Empty,
  Field,
  KeyValue,
  Menu,
  MenuItem,
  Notice,
  PageHead,
  Pager,
  RowsSkeleton,
  SearchInput,
  SettingRow,
  Stat,
  StatsSkeleton,
  Switch,
  Tabs,
  Ticks,
} from '../../components/ui.js'
import { BarChart, Donut } from '../../components/charts.js'
import type { PlatformOverview } from './SuperAdminDashboard.js'
import { ReminderFunnelCard } from '../../components/ReminderFunnel.js'
import {
  AlertIcon,
  BagIcon,
  BuildingIcon,
  CheckCircleIcon,
  DownloadIcon,
  LedgerIcon,
  MoreIcon,
  PlugIcon,
  RupeeIcon,
  ShieldIcon,
  StoreIcon,
  TrendUpIcon,
  UsersIcon,
  WalletIcon,
} from '../../components/icons.js'

const PER_PAGE = 25

// ── Shared shapes ───────────────────────────────────────────────────────────

interface AdminMerchant {
  id: string
  name: string
  email: string | null
  phone: string | null
  status: string
  plan: string
  currency: string
  shops: number
  walletBalance: string
  walletLowSince: string | null
  commissionRateOverride: number | null
  walletFloorUnlimited: boolean
  chargeCodOnConfirm: boolean
  gmv: string
  orders: number
  commission: string
  joinedAt: string
}

interface AdminUser {
  id: string
  name: string
  email: string | null
  phone: string | null
  role: string
  merchant: { id: string; name: string; currency: string; walletBalance: string; walletLowSince: string | null } | null
  shopDomain: string | null
  onboarded: boolean
  joinedAt: string
  lastSeenAt: string | null
}

interface ProductLine {
  name: string
  quantity: number
  url: string | null
}

/** Each product on its own line, linked to the storefront page when known. */
function ProductLines({ products }: { products: ProductLine[] }): React.ReactNode {
  if (products.length === 0) return null

  return (
    <span className="table__products">
      {products.map((product, index) => (
        <span key={`${product.name}-${index}`} className="table__sub">
          {product.quantity > 1 ? `${product.quantity} × ` : ''}
          {product.url ? (
            <a href={product.url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
              {product.name}
            </a>
          ) : (
            product.name
          )}
        </span>
      ))}
    </span>
  )
}

type SortDir = 'asc' | 'desc'

/**
 * A header that sorts the *whole* list server-side. Clicking the active column
 * flips its direction; clicking another starts it at the direction that column
 * is most often wanted in.
 */
function SortTh<T extends string>({
  label,
  column,
  sort,
  dir,
  onSort,
  firstDir = 'asc',
  className,
}: {
  label: string
  column: T
  sort: T
  dir: SortDir
  onSort: (column: T, dir: SortDir) => void
  firstDir?: SortDir
  className?: string
}): React.ReactNode {
  const active = sort === column

  return (
    <th className={className} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button
        type="button"
        className={active ? 'th-sort is-active' : 'th-sort'}
        onClick={() => onSort(column, active ? (dir === 'asc' ? 'desc' : 'asc') : firstDir)}
      >
        {label}
        <span aria-hidden="true">{active ? (dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </button>
    </th>
  )
}

interface LedgerEntry {
  id: string
  reference: string
  at: string
  kind: string
  type: 'debit' | 'credit'
  status: string
  amount: string
  balanceAfter: string
  currency: string
  description: string | null
  merchant: { id: string; name: string } | null
}

interface Paged<T> {
  items: T[]
  total: number
  page: number
  perPage: number
  hasMore: boolean
}

/** The recovery path for a user who lost the phone or app behind their 2FA. */
function ResetTwoFactorButton({ userId, name }: { userId: string; name: string }): React.ReactNode {
  const queryClient = useQueryClient()
  const reset = useMutation({
    mutationFn: () => api(`/v1/admin/users/${userId}/reset-2fa`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries(),
  })

  return (
    <button
      type="button"
      className="btn btn--danger"
      disabled={reset.isPending}
      title={reset.error ? (reset.error as Error).message : undefined}
      onClick={() => {
        if (window.confirm(`Turn off two-step verification for ${name}? Do this only once you have confirmed who is asking.`)) reset.mutate()
      }}
    >
      <ShieldIcon size={16} />
      {reset.isPending ? 'Resetting…' : 'Reset 2FA'}
    </button>
  )
}

// ── Merchants ───────────────────────────────────────────────────────────────

export function AdminMerchantsPage(): React.ReactNode {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | 'paused'>('all')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<AdminMerchant | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'merchants', search, status, page],
    queryFn: () =>
      api<Paged<AdminMerchant>>('/v1/admin/merchants', {
        query: { q: search, status: status === 'all' ? '' : status, page, perPage: PER_PAGE },
      }),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  const total = data?.total ?? 0

  return (
    <>
      <PageHead title="Merchants" subtitle="Every account on the platform, with its wallet and trailing volume." />

      <WalletAlertTemplateCard />

      <Card flush>
        <Tabs
          value={status}
          onChange={(key) => reset(() => setStatus(key))}
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'paused', label: 'Paused' },
          ]}
        />

        <div className="toolbar">
          <SearchInput value={search} onChange={(next) => reset(() => setSearch(next))} placeholder="Name, email or phone" />
        </div>

        {error && (
          <Empty error title="Could not load merchants">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<BuildingIcon size={24} />} title="No merchants match those filters" />
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Plan</th>
                    <th className="right">Stores</th>
                    <th className="right">Wallet</th>
                    <th className="right">GMV (30d)</th>
                    <th className="right">Orders</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((merchant) => {
                    const negative = Number(merchant.walletBalance) < 0
                    return (
                      <tr key={merchant.id} className="is-clickable" onClick={() => setSelected(merchant)}>
                        <td>
                          <span className="table__cell">
                            <span className="avatar avatar--sm avatar--muted">{initials(merchant.name)}</span>
                            <span>
                              {merchant.name}
                              <span className="table__sub">{merchant.email ?? merchant.phone ?? '—'}</span>
                            </span>
                          </span>
                        </td>
                        <td>{humanise(merchant.plan)}</td>
                        <td className="right num">{merchant.shops}</td>
                        <td
                          className="right num"
                          style={{ color: negative ? 'var(--bad)' : merchant.walletLowSince ? 'var(--warn)' : undefined }}
                        >
                          {moneyExact(merchant.walletBalance, merchant.currency)}
                        </td>
                        <td className="right num">{money(merchant.gmv, merchant.currency)}</td>
                        <td className="right num">{merchant.orders.toLocaleString('en-IN')}</td>
                        <td>
                          <Badge tone={merchant.status}>{humanise(merchant.status)}</Badge>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="merchants"
            />
          </>
        )}
      </Card>

      {selected && (
        <MerchantDrawer merchant={selected} onClose={() => setSelected(null)} />
      )}
    </>
  )
}

/**
 * The one WhatsApp template this platform sends to a merchant about
 * themselves rather than to a shopper about an order. Has to be an already
 * Meta-approved template — free text is not a thing WhatsApp's business API
 * allows — so this card only names which approved template to use; approving
 * one happens on Wakit's side.
 */
function WalletAlertTemplateCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['admin', 'wallet-alert-template'],
    queryFn: () => api<{ template: string }>('/v1/admin/wallet-alert/template'),
  })

  const [template, setTemplate] = useState('')
  const [touched, setTouched] = useState(false)

  const current = touched ? template : data?.template ?? template

  const save = useMutation({
    mutationFn: () => api<{ template: string }>('/v1/admin/wallet-alert/template', { method: 'PUT', body: { template: current.trim() } }),
    onSuccess: () => {
      setTouched(false)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alert-template'] })
    },
  })

  return (
    <Card title="Wallet alert on WhatsApp" subtitle="The approved template used when an operator sends a merchant their own wallet balance.">
      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      <div className="row" style={{ gap: 8 }}>
        <input
          className="input"
          placeholder="e.g. wallet_balance_alert"
          value={current}
          onChange={(event) => {
            setTemplate(event.target.value)
            setTouched(true)
          }}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn btn--primary"
          disabled={!touched || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <span className="field__hint">
        Sent with two values: the merchant&rsquo;s name and their current wallet balance. Never billed to
        anyone — this message has no price.
      </span>
    </Card>
  )
}

/**
 * Sends one merchant their own current wallet balance, free, on an
 * operator's say — one button per channel (WhatsApp, email). Not queued and
 * not the order-notification path: this is a courtesy to the merchant about
 * their own account, not a sale to a shopper.
 */
function WalletAlertButton({ endpoint, label }: { endpoint: string; label: string }): React.ReactNode {
  const send = useMutation({
    mutationFn: () => api<{ sent: boolean }>(endpoint, { method: 'POST' }),
  })

  if (send.isSuccess) {
    return (
      <div style={{ marginTop: 8 }}>
        <Notice kind="ok" title="Sent" />
      </div>
    )
  }

  return (
    <>
      {send.error && (
        <div style={{ marginTop: 8 }}>
          <Notice kind="error" title="Could not send">
            {(send.error as Error).message}
          </Notice>
        </div>
      )}
      <button
        type="button"
        className="btn btn--block"
        style={{ marginTop: 8 }}
        disabled={send.isPending}
        onClick={() => send.mutate()}
      >
        {send.isPending ? 'Sending…' : label}
      </button>
    </>
  )
}

/**
 * One merchant's detail, with the two billing overrides an operator can set
 * on them: their own commission rate, and whether their wallet's debit floor
 * applies to them at all.
 *
 * Everything else here is read-only on purpose — pausing an account or
 * crediting a wallet moves real money through a path this panel does not
 * have. Billing overrides are different: they change what a *future* order
 * is charged, never a balance directly, so the risk is the same shape as the
 * platform-wide commission settings an operator already edits elsewhere.
 */
function MerchantDrawer({ merchant, onClose }: { merchant: AdminMerchant; onClose: () => void }): React.ReactNode {
  const queryClient = useQueryClient()
  const [rateInput, setRateInput] = useState(merchant.commissionRateOverride === null ? '' : String(merchant.commissionRateOverride))
  const [unlimited, setUnlimited] = useState(merchant.walletFloorUnlimited)
  const [codOnConfirm, setCodOnConfirm] = useState(merchant.chargeCodOnConfirm)

  const save = useMutation({
    mutationFn: () =>
      api<{ commissionRateOverride: number | null; walletFloorUnlimited: boolean; chargeCodOnConfirm: boolean }>(
        `/v1/admin/merchants/${merchant.id}/billing`,
        {
          method: 'PATCH',
          body: {
            rate: rateInput.trim() === '' ? null : Number(rateInput),
            walletFloorUnlimited: unlimited,
            chargeCodOnConfirm: codOnConfirm,
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] })
    },
  })

  const dirty =
    rateInput.trim() !== (merchant.commissionRateOverride === null ? '' : String(merchant.commissionRateOverride)) ||
    unlimited !== merchant.walletFloorUnlimited ||
    codOnConfirm !== merchant.chargeCodOnConfirm

  return (
    <Drawer
      open
      onClose={onClose}
      title={merchant.name}
      subtitle={`Joined ${dateTime(merchant.joinedAt).split(',')[0]} · ${merchant.shops} store${merchant.shops === 1 ? '' : 's'}`}
    >
      <Notice kind="info" title="Read-only, except billing below">
        Pausing an account or crediting a wallet directly needs its own audited endpoint, which has not shipped.
      </Notice>

      <DrawerSection title="Contact">
        <KeyValue
          rows={[
            { label: 'Email', value: merchant.email ?? '—' },
            { label: 'Phone', value: merchant.phone ?? '—' },
            { label: 'Plan', value: <Badge tone="brand">{humanise(merchant.plan)}</Badge> },
            { label: 'Status', value: <Badge tone={merchant.status}>{humanise(merchant.status)}</Badge> },
          ]}
        />
      </DrawerSection>

      <DrawerSection title="Wallet">
        <KeyValue
          rows={[
            {
              label: 'Balance',
              value: moneyExact(merchant.walletBalance, merchant.currency),
              kind: Number(merchant.walletBalance) < 0 ? 'debit' : 'credit',
            },
            { label: 'Low since', value: merchant.walletLowSince ? ago(merchant.walletLowSince) : '—' },
          ]}
        />
        <WalletAlertButton
          endpoint={`/v1/admin/merchants/${merchant.id}/wallet-alert`}
          label="Send wallet alert on WhatsApp"
        />
        <WalletAlertButton
          endpoint={`/v1/admin/merchants/${merchant.id}/wallet-alert-email`}
          label="Send wallet alert by email"
        />
      </DrawerSection>

      <DrawerSection title="Trailing 30 days">
        <KeyValue
          rows={[
            { label: 'GMV', value: money(merchant.gmv, merchant.currency) },
            { label: 'Settled orders', value: merchant.orders.toLocaleString('en-IN') },
            { label: 'Commission earned', value: money(merchant.commission, merchant.currency), kind: 'total' },
          ]}
        />
      </DrawerSection>

      <DrawerSection title="This merchant's billing">
        <p className="faint" style={{ marginTop: -4, marginBottom: 12 }}>
          Overrides the plan fee and the platform floor for this merchant alone.
        </p>

        {save.error && (
          <Notice kind="error" title="Could not save">
            {(save.error as Error).message}
          </Notice>
        )}

        {save.isSuccess && !dirty && <Notice kind="ok" title="Saved" />}

        <label className="field">
          <span className="field__label">Commission rate (%)</span>
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            step="0.01"
            placeholder={`Default — plan fee for ${humanise(merchant.plan)}`}
            value={rateInput}
            onChange={(event) => setRateInput(event.target.value)}
          />
          <span className="field__hint">Blank clears the override and falls back to the plan fee.</span>
        </label>

        <label className="checkbox-row" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={unlimited} onChange={(event) => setUnlimited(event.target.checked)} />
          <span>Wallet may go negative with no floor</span>
        </label>

        <label className="checkbox-row" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={codOnConfirm}
            onChange={(event) => setCodOnConfirm(event.target.checked)}
          />
          <span>Charge COD commission the moment an order is mirrored</span>
        </label>
        <span className="field__hint">
          For a storefront that never reports a later "Processing"/"Completed" status back to this
          platform — every non-cancelled COD order is charged immediately instead of waiting for a
          payment state this merchant's plugin will never send.
        </span>

        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={!dirty || save.isPending}
          style={{ marginTop: 12 }}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save billing overrides'}
        </button>
      </DrawerSection>
    </Drawer>
  )
}

// ── Users ───────────────────────────────────────────────────────────────────

type UserSort = 'name' | 'joined' | 'lastSeen' | 'wallet'

const NO_USER_FILTERS = { role: '', onboarding: '', wallet: '', activity: '' }

const USER_FILTERS: Array<{ key: keyof typeof NO_USER_FILTERS; label: string; options: Array<[string, string]> }> = [
  { key: 'role', label: 'Role', options: [['merchant', 'Merchant'], ['operator', 'Operator'], ['unassigned', 'Unassigned']] },
  { key: 'onboarding', label: 'Onboarding', options: [['complete', 'Complete'], ['in_progress', 'In progress']] },
  { key: 'wallet', label: 'Wallet', options: [['low', 'Low balance'], ['negative', 'Negative']] },
  {
    key: 'activity',
    label: 'Activity',
    options: [
      ['online', 'Online now'],
      ['day', 'Seen in 24 hours'],
      ['week', 'Seen in 7 days'],
      ['inactive', 'Inactive 30+ days'],
      ['never', 'Never seen'],
    ],
  },
]

export function AdminUsersPage(): React.ReactNode {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [search, setSearch] = useState(params.get('q') ?? '')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<UserSort>('joined')
  const [dir, setDir] = useState<SortDir>('desc')
  // Other screens link here pre-filtered, e.g. `?wallet=low` from Wallet Alerts.
  const [filters, setFilters] = useState(() => ({
    ...NO_USER_FILTERS,
    ...Object.fromEntries(Object.keys(NO_USER_FILTERS).map((key) => [key, params.get(key) ?? ''])),
  }))

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'users', search, filters, sort, dir, page],
    queryFn: () =>
      api<Paged<AdminUser>>('/v1/admin/users', {
        query: { q: search, ...filters, sort, dir, page, perPage: PER_PAGE },
      }),
  })

  const filtered = search !== '' || Object.values(filters).some((value) => value !== '')

  function setFilter(key: keyof typeof NO_USER_FILTERS, value: string): void {
    setFilters((current) => ({ ...current, [key]: value }))
    setPage(1)
  }

  function onSort(column: UserSort, next: SortDir): void {
    setSort(column)
    setDir(next)
    setPage(1)
  }

  const total = data?.total ?? 0

  return (
    <>
      <PageHead title="Users" subtitle="Everyone with an account, and which merchant they belong to." />

      <Card flush>
        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={(next) => {
              setSearch(next)
              setPage(1)
            }}
            placeholder="Name, email or phone"
          />

          {USER_FILTERS.map((filter) => (
            <select
              key={filter.key}
              className="select"
              aria-label={filter.label}
              value={filters[filter.key]}
              onChange={(event) => setFilter(filter.key, event.target.value)}
            >
              <option value="">{filter.label}: All</option>
              {filter.options.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ))}

          {filtered && (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                setSearch('')
                setFilters(NO_USER_FILTERS)
                setPage(1)
              }}
            >
              Clear
            </button>
          )}

          {data && (
            <span className="faint" style={{ marginLeft: 'auto', fontSize: '.82rem' }}>
              {total.toLocaleString('en-IN')} user{total === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {error && (
          <Empty error title="Could not load users">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && <Empty icon={<UsersIcon size={24} />} title="No users match these filters" />}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <SortTh label="User" column="name" sort={sort} dir={dir} onSort={onSort} />
                    <th>Role</th>
                    <th>Merchant</th>
                    <th>Store</th>
                    <SortTh label="Wallet" column="wallet" sort={sort} dir={dir} onSort={onSort} firstDir="desc" className="right" />
                    <SortTh label="Joined" column="joined" sort={sort} dir={dir} onSort={onSort} firstDir="desc" />
                    <SortTh label="Last seen" column="lastSeen" sort={sort} dir={dir} onSort={onSort} firstDir="desc" />
                    <th>Onboarding</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id} className="is-clickable" onClick={() => navigate(user.id)}>
                      <td>
                        <span className="table__cell">
                          <span className="avatar avatar--sm avatar--muted">{initials(user.name)}</span>
                          <span>
                            {user.name}
                            <span className="table__sub">{user.email ?? user.phone ?? '—'}</span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <Badge tone={user.role === 'Operator' ? 'accent' : user.role === 'Merchant' ? 'brand' : undefined}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>{user.merchant ? user.merchant.name : <span className="faint">Platform</span>}</td>
                      <td>{user.shopDomain ?? <span className="faint">—</span>}</td>
                      {user.merchant ? (
                        <td
                          className="right num"
                          style={{
                            color:
                              Number(user.merchant.walletBalance) < 0
                                ? 'var(--bad)'
                                : user.merchant.walletLowSince
                                  ? 'var(--warn)'
                                  : undefined,
                          }}
                        >
                          {moneyExact(user.merchant.walletBalance, user.merchant.currency)}
                        </td>
                      ) : (
                        <td className="right">
                          <span className="faint">—</span>
                        </td>
                      )}
                      <td className="num">{dateTime(user.joinedAt)}</td>
                      <td>{user.lastSeenAt ? <LastSeen at={user.lastSeenAt} /> : <span className="faint">Never</span>}</td>
                      <td>
                        <Badge tone={user.onboarded ? 'ok' : 'warn'}>{user.onboarded ? 'Complete' : 'In progress'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="users"
            />
          </>
        )}
      </Card>
    </>
  )
}

interface AdminUserDetail {
  user: {
    id: string
    name: string
    email: string | null
    phone: string | null
    role: string
    teamRole: string
    signIn: 'google' | 'email' | 'phone'
    emailVerified: boolean
    twoFactor: 'totp' | 'whatsapp' | null
    onboarded: boolean
    joinedAt: string
    lastSeenAt: string | null
  }
  merchant: {
    id: string
    name: string
    email: string | null
    phone: string | null
    status: string
    plan: string
    planRenewsAt: string | null
    currency: string
    walletBalance: string
    walletLowSince: string | null
    teamSize: number
    joinedAt: string
    walletAlert: WalletAlertSchedule
  } | null
  sales: {
    daily: Array<{ date: string; orders: number; revenue: number }>
    hourly: Array<{ hour: number; orders: number; revenue: number }>
    monthly: Array<{ month: string; orders: number; revenue: number }>
  }
  stores: Array<{
    id: string
    domain: string
    name: string
    platform: string
    currency: string
    status: string
    connectedAt: string | null
    lastSeenAt: string | null
    revenue: string
    orders: number
  }>
  orders: {
    total: number
    settled: number
    gmv: string
    commission: string
    byStatus: Array<{ status: string; count: number; amount: string }>
    recent: Array<{
      id: string
      orderNumber: string
      customerName: string | null
      phone: string | null
      total: string
      currency: string
      paymentMethod: string | null
      paymentStatus: string
      placedAt: string
      store: string | null
      products: ProductLine[]
    }>
  }
  wallet: {
    transactions: number
    credited: string
    debited: string
    recharged: string
    commission: string
    recent: Array<{
      id: string
      reference: string
      at: string
      kind: string
      type: 'debit' | 'credit'
      status: string
      amount: string
      balanceAfter: string
      currency: string
      description: string | null
    }>
  }
}

interface WalletAlertSchedule {
  frequency: 'off' | 'hourly' | 'daily' | 'weekly'
  email: boolean
  whatsapp: boolean
  threshold: number | null
  lastSentAt: string | null
}

/** "Online" inside the stamp window, otherwise how long ago. */
function LastSeen({ at }: { at: string }): React.ReactNode {
  const online = Date.now() - new Date(at).getTime() < 10 * 60_000

  return online ? <Badge tone="ok">Online</Badge> : <span title={dateTime(at)}>{ago(at)}</span>
}

type SalesView = 'daily' | 'hourly' | 'monthly'

function SalesCharts({ sales, currency }: { sales: AdminUserDetail['sales']; currency: string }): React.ReactNode {
  const [view, setView] = useState<SalesView>('daily')

  const rows =
    view === 'daily'
      ? sales.daily.map((row) => ({
          label: new Date(`${row.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
          orders: row.orders,
          revenue: row.revenue,
        }))
      : view === 'hourly'
        ? sales.hourly.map((row) => ({
            label: `${String(row.hour).padStart(2, '0')}:00`,
            orders: row.orders,
            revenue: row.revenue,
          }))
        : sales.monthly.map((row) => ({
            label: new Date(`${row.month}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
            orders: row.orders,
            revenue: row.revenue,
          }))

  const revenue = rows.reduce((sum, row) => sum + row.revenue, 0)
  const orders = rows.reduce((sum, row) => sum + row.orders, 0)

  const window =
    view === 'daily'
      ? 'Settled sales per day, last 30 days (IST).'
      : view === 'hourly'
        ? 'Settled sales by hour of day, last 30 days (IST).'
        : 'Settled sales per month, last 12 months.'

  return (
    <Card
      flush
      title="Sales"
      subtitle={`${window} ${money(revenue.toFixed(2), currency)} from ${orders.toLocaleString('en-IN')} orders.`}
    >
      <Tabs
        value={view}
        onChange={setView}
        tabs={[
          { key: 'daily', label: 'Per day' },
          { key: 'hourly', label: 'By hour' },
          { key: 'monthly', label: 'Monthly' },
        ]}
      />
      <div style={{ padding: 16 }}>
        {orders === 0 ? (
          <Empty icon={<TrendUpIcon size={24} />} title="No settled sales in this window" />
        ) : (
          <BarChart
            labels={rows.map((row) => row.label)}
            bars={{ key: 'revenue', label: 'Revenue', color: 'var(--brand)', values: rows.map((row) => row.revenue) }}
            line={{ key: 'orders', label: 'Orders', color: 'var(--ok)', values: rows.map((row) => row.orders) }}
          />
        )}
      </div>
    </Card>
  )
}

/**
 * Turns a live store off from the operator side — the same end state as an
 * uninstall: its checkout stops at once, nothing is deleted, and reconnecting
 * from the storefront brings it back. Domain typed back, like detach.
 */
function DisconnectStoreButton({ store, userId }: { store: { id: string; domain: string }; userId: string }): React.ReactNode {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')

  const disconnect = useMutation({
    mutationFn: () =>
      api<{ disconnected: boolean }>(`/v1/admin/stores/${store.id}/disconnect`, {
        method: 'POST',
        body: { domain: typed.trim() },
      }),
    onSuccess: () => {
      setOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
    },
  })

  if (!open) {
    return (
      <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
        Disconnect
      </button>
    )
  }

  return (
    <span className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <input
        className="input"
        style={{ width: 200, height: 30 }}
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        placeholder={`Type ${store.domain}`}
        autoComplete="off"
        spellCheck={false}
        autoFocus
      />
      <button
        type="button"
        className="btn btn--sm btn--danger"
        disabled={typed.trim() !== store.domain || disconnect.isPending}
        title="Stops this store's checkout now. Orders and settings are kept."
        onClick={() => disconnect.mutate()}
      >
        {disconnect.isPending ? 'Disconnecting…' : 'Confirm'}
      </button>
      <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {disconnect.error && (
        <span style={{ color: 'var(--bad)', fontSize: '.8rem' }}>{(disconnect.error as Error).message}</span>
      )}
    </span>
  )
}

/**
 * Send the merchant their wallet balance now, on either channel, and set how
 * often the hourly job warns them by itself while the wallet stays low.
 */
function WalletAlertCard({
  merchant,
  userId,
}: {
  merchant: NonNullable<AdminUserDetail['merchant']>
  userId: string
}): React.ReactNode {
  const queryClient = useQueryClient()
  const saved = merchant.walletAlert
  const [frequency, setFrequency] = useState(saved.frequency)
  const [email, setEmail] = useState(saved.email)
  const [whatsapp, setWhatsapp] = useState(saved.whatsapp)
  const [threshold, setThreshold] = useState(saved.threshold === null ? '' : String(saved.threshold))

  const save = useMutation({
    mutationFn: () =>
      api<WalletAlertSchedule>(`/v1/admin/merchants/${merchant.id}/wallet-alert-schedule`, {
        method: 'PUT',
        body: { frequency, email, whatsapp, threshold: threshold.trim() === '' ? null : Number(threshold) },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId] })
    },
  })

  const dirty =
    frequency !== saved.frequency ||
    email !== saved.email ||
    whatsapp !== saved.whatsapp ||
    threshold.trim() !== (saved.threshold === null ? '' : String(saved.threshold))

  return (
    <Card title="Low wallet warning" subtitle="Send the balance now, or schedule automatic warnings while the wallet stays low.">
      <div className="grid grid--2" style={{ gap: 8 }}>
        <WalletAlertButton endpoint={`/v1/admin/merchants/${merchant.id}/wallet-alert`} label="Send on WhatsApp now" />
        <WalletAlertButton endpoint={`/v1/admin/merchants/${merchant.id}/wallet-alert-email`} label="Send by email now" />
      </div>

      <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
        {save.error && (
          <Notice kind="error" title="Could not save">
            {(save.error as Error).message}
          </Notice>
        )}
        {save.isSuccess && !dirty && <Notice kind="ok" title="Schedule saved" />}

        <label className="field">
          <span className="field__label">Automatic warning</span>
          <select
            className="input"
            value={frequency}
            onChange={(event) => setFrequency(event.target.value as WalletAlertSchedule['frequency'])}
          >
            <option value="off">Off</option>
            <option value="hourly">Every hour</option>
            <option value="daily">Once a day</option>
            <option value="weekly">Once a week</option>
          </select>
        </label>

        <label className="field">
          <span className="field__label">Warn when balance is below ({merchant.currency})</span>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="Default — the platform wallet floor"
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
          />
          <span className="field__hint">Blank uses the platform floor (the wallet&rsquo;s &ldquo;low since&rdquo; flag).</span>
        </label>

        <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="checkbox-row">
            <input type="checkbox" checked={email} onChange={(event) => setEmail(event.target.checked)} />
            <span>Email{merchant.email ? '' : ' (no email on file)'}</span>
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={whatsapp} onChange={(event) => setWhatsapp(event.target.checked)} />
            <span>WhatsApp{merchant.phone ? '' : ' (no phone on file)'}</span>
          </label>
        </div>

        <span className="field__hint">
          Last automatic warning:{' '}
          {saved.lastSentAt ? `${dateTime(saved.lastSentAt)} (${ago(saved.lastSentAt)})` : 'never'}.
        </span>

        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save schedule'}
        </button>
      </div>
    </Card>
  )
}

/** Every wallet movement for one merchant, paged, with an added/debited filter. */
function WalletHistory({ merchantId }: { merchantId: string }): React.ReactNode {
  const [type, setType] = useState<'all' | 'credit' | 'debit'>('all')
  const [page, setPage] = useState(1)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'ledger', 'merchant', merchantId, type, page],
    queryFn: () =>
      api<Paged<LedgerEntry> & { summary: { credited: string; debited: string; net: string } }>('/v1/admin/ledger', {
        query: { merchantId, type: type === 'all' ? '' : type, page, perPage: PER_PAGE },
      }),
  })

  const total = data?.total ?? 0
  const currency = data?.items[0]?.currency ?? 'INR'

  return (
    <Card
      flush
      title={`Wallet transactions (${total.toLocaleString('en-IN')})`}
      subtitle={
        data
          ? `Added ${moneyExact(data.summary.credited, currency)}, debited ${moneyExact(data.summary.debited, currency)}, net ${moneyExact(data.summary.net, currency)}.`
          : undefined
      }
    >
      <Tabs
        value={type}
        onChange={(key) => {
          setType(key)
          setPage(1)
        }}
        tabs={[
          { key: 'all', label: 'All' },
          { key: 'credit', label: 'Added' },
          { key: 'debit', label: 'Debited' },
        ]}
      />

      {error && (
        <Empty error title="Could not load wallet transactions">
          {(error as Error).message}
        </Empty>
      )}

      {isPending && <RowsSkeleton rows={6} />}

      {data && data.items.length === 0 && <Empty icon={<LedgerIcon size={24} />} title="No wallet transactions" />}

      {data && data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Kind</th>
                  <th>Reference</th>
                  <th className="right">Amount</th>
                  <th>Status</th>
                  <th className="right">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {dateTime(entry.at)}
                      <span className="table__sub">{ago(entry.at)}</span>
                    </td>
                    <td>{humanise(entry.kind)}</td>
                    <td>
                      <code>{entry.reference}</code>
                      {entry.description && <span className="table__sub">{entry.description}</span>}
                    </td>
                    <td className="right num" style={{ color: entry.type === 'credit' ? 'var(--ok)' : 'var(--bad)' }}>
                      {entry.type === 'credit' ? '+ ' : '− '}
                      {moneyExact(entry.amount, entry.currency)}
                    </td>
                    <td>
                      <Badge tone={entry.status === 'settled' ? 'ok' : 'accent'}>{humanise(entry.status)}</Badge>
                    </td>
                    <td className="right num">{moneyExact(entry.balanceAfter, entry.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            page={page}
            pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
            total={total}
            perPage={PER_PAGE}
            onPage={setPage}
            noun="transactions"
          />
        </>
      )}
    </Card>
  )
}

export function AdminUserPage(): React.ReactNode {
  const { id = '' } = useParams<{ id: string }>()

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api<AdminUserDetail>(`/v1/admin/users/${id}`),
  })

  const crumbs = [{ label: 'Users', to: '/admin/users' }, { label: data?.user.name ?? 'User' }]

  if (error) {
    return (
      <>
        <PageHead title="User" crumbs={crumbs} />
        <Card>
          <Empty error title="Could not load this user">
            {(error as Error).message}
          </Empty>
        </Card>
      </>
    )
  }

  if (isPending || !data) {
    return (
      <>
        <PageHead title="User" crumbs={crumbs} />
        <StatsSkeleton count={4} />
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      </>
    )
  }

  const { user, merchant, stores, orders, wallet } = data
  const currency = merchant?.currency ?? stores[0]?.currency ?? 'INR'
  const negative = merchant ? Number(merchant.walletBalance) < 0 : false
  const liveStores = stores.filter((store) => store.status !== 'uninstalled').length

  return (
    <>
      <PageHead
        title={user.name}
        subtitle={[user.email, user.phone].filter(Boolean).join(' · ') || undefined}
        crumbs={crumbs}
        actions={user.twoFactor ? <ResetTwoFactorButton userId={user.id} name={user.name} /> : undefined}
      />

      <section className="grid grid--4">
        <Stat
          label="Wallet balance"
          value={
            merchant ? (
              <span style={{ color: negative ? 'var(--bad)' : merchant.walletLowSince ? 'var(--warn)' : undefined }}>
                {moneyExact(merchant.walletBalance, merchant.currency)}
              </span>
            ) : (
              '—'
            )
          }
          icon={<WalletIcon size={20} />}
          tone="var(--brand)"
        />
        <Stat
          label="Orders"
          value={orders.total.toLocaleString('en-IN')}
          icon={<BagIcon size={20} />}
          tone="var(--accent)"
          footnote={`${orders.settled.toLocaleString('en-IN')} settled`}
        />
        <Stat
          label="GMV (all time)"
          value={money(orders.gmv, currency)}
          icon={<RupeeIcon size={20} />}
          tone="var(--ok)"
          footnote={`${money(orders.commission, currency)} commission`}
        />
        <Stat
          label="Stores"
          value={stores.length.toLocaleString('en-IN')}
          icon={<StoreIcon size={20} />}
          tone="var(--warn)"
          footnote={`${liveStores} live`}
        />
      </section>

      <div className="grid grid--2">
        <Card title="Account">
          <KeyValue
            rows={[
              {
                label: 'Role',
                value: (
                  <Badge tone={user.role === 'Operator' ? 'accent' : user.role === 'Merchant' ? 'brand' : undefined}>
                    {user.role}
                  </Badge>
                ),
              },
              { label: 'Team role', value: humanise(user.teamRole) },
              { label: 'Email', value: user.email ?? '—' },
              { label: 'Phone', value: user.phone ?? '—' },
              { label: 'Signs in with', value: humanise(user.signIn) },
              { label: 'Email verified', value: user.email ? (user.emailVerified ? 'Yes' : 'No') : '—' },
              {
                label: 'Onboarding',
                value: <Badge tone={user.onboarded ? 'ok' : 'warn'}>{user.onboarded ? 'Complete' : 'In progress'}</Badge>,
              },
              { label: 'Joined', value: dateTime(user.joinedAt) },
              { label: 'Last seen', value: user.lastSeenAt ? <LastSeen at={user.lastSeenAt} /> : 'Never' },
            ]}
          />
        </Card>

        <Card title="Merchant">
          {merchant ? (
            <KeyValue
              rows={[
                { label: 'Name', value: merchant.name },
                { label: 'Status', value: <Badge tone={merchant.status}>{humanise(merchant.status)}</Badge> },
                { label: 'Plan', value: humanise(merchant.plan) },
                { label: 'Plan renews', value: merchant.planRenewsAt ? dateTime(merchant.planRenewsAt) : '—' },
                { label: 'Team members', value: merchant.teamSize },
                { label: 'Recharged', value: moneyExact(wallet.recharged, merchant.currency), kind: 'credit' },
                { label: 'Commission paid', value: moneyExact(wallet.commission, merchant.currency), kind: 'debit' },
                { label: 'Wallet transactions', value: wallet.transactions.toLocaleString('en-IN') },
                { label: 'Merchant since', value: dateTime(merchant.joinedAt) },
              ]}
            />
          ) : (
            <Empty icon={<BuildingIcon size={24} />} title="Not attached to a merchant" />
          )}
        </Card>
      </div>

      <SalesCharts sales={data.sales} currency={currency} />

      {merchant && <WalletAlertCard key={merchant.id} merchant={merchant} userId={user.id} />}

      <Card flush title={`Stores (${stores.length})`}>
        {stores.length === 0 ? (
          <Empty icon={<StoreIcon size={24} />} title="No stores connected" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Connected</th>
                  <th>Last seen</th>
                  <th className="right">Orders</th>
                  <th className="right">GMV</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <tr key={store.id}>
                    <td>
                      {store.name}
                      {store.name !== store.domain && <span className="table__sub">{store.domain}</span>}
                    </td>
                    <td>{humanise(store.platform)}</td>
                    <td>
                      <Badge tone={store.status === 'connected' ? 'ok' : store.status === 'pending' ? 'warn' : undefined}>
                        {humanise(store.status)}
                      </Badge>
                    </td>
                    <td>{store.connectedAt ? dateTime(store.connectedAt) : '—'}</td>
                    <td>{store.lastSeenAt ? ago(store.lastSeenAt) : '—'}</td>
                    <td className="right num">{store.orders.toLocaleString('en-IN')}</td>
                    <td className="right num">{money(store.revenue, store.currency)}</td>
                    <td className="right">
                      {store.status !== 'uninstalled' && <DisconnectStoreButton store={store} userId={user.id} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card flush title={`Orders (${orders.total.toLocaleString('en-IN')})`} subtitle="Latest 15, with a count per payment status.">
        {orders.total === 0 ? (
          <Empty icon={<BagIcon size={24} />} title="No orders yet" />
        ) : (
          <>
            <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
              {orders.byStatus.map((row) => (
                <Badge key={row.status} tone={row.status}>
                  {humanise(row.status)}: {row.count.toLocaleString('en-IN')} · {money(row.amount, currency)}
                </Badge>
              ))}
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Store</th>
                    <th>Placed</th>
                    <th>Method</th>
                    <th className="right">Total</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.recent.map((order) => (
                    <tr key={order.id}>
                      <td>
                        {order.orderNumber}
                        <span className="table__sub">{order.customerName ?? order.phone ?? '—'}</span>
                        <ProductLines products={order.products} />
                      </td>
                      <td>{order.store ?? '—'}</td>
                      <td>
                        {dateTime(order.placedAt)}
                        <span className="table__sub">{ago(order.placedAt)}</span>
                      </td>
                      <td>{order.paymentMethod ? humanise(order.paymentMethod) : '—'}</td>
                      <td className="right num">{money(order.total, order.currency)}</td>
                      <td>
                        <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {merchant && <WalletHistory merchantId={merchant.id} />}
    </>
  )
}

// ── Ledger ──────────────────────────────────────────────────────────────────

const LEDGER_VIEWS: Record<string, { title: string; subtitle: string; kind: string }> = {
  ledger: { title: 'Ledger', subtitle: 'Every movement of money across every merchant wallet.', kind: '' },
  recharges: { title: 'Recharges', subtitle: 'Merchant wallet top-ups.', kind: 'recharge' },
  commission: { title: 'Commission', subtitle: 'Commission debited from merchant wallets.', kind: 'commission' },
}

export function AdminLedgerPage(): React.ReactNode {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const segment = pathname.split('/').filter(Boolean).at(-1) ?? 'ledger'
  const key = segment in LEDGER_VIEWS ? segment : 'ledger'
  const config = LEDGER_VIEWS[key] ?? LEDGER_VIEWS.ledger!

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'ledger', key, page],
    queryFn: () =>
      api<Paged<LedgerEntry> & { summary: { credited: string; debited: string; net: string } }>('/v1/admin/ledger', {
        query: { kind: config.kind, page, perPage: PER_PAGE },
      }),
  })

  const total = data?.total ?? 0

  return (
    <>
      <PageHead title={config.title} subtitle={config.subtitle} crumbs={[{ label: 'Wallet & ledger' }, { label: config.title }]} />

      {isPending && <StatsSkeleton count={4} />}

      {data && (
        <section className="grid grid--4">
          <Stat label="Credited" value={money(data.summary.credited)} icon={<TrendUpIcon size={20} />} tone="var(--ok)" />
          <Stat label="Debited" value={money(data.summary.debited)} icon={<LedgerIcon size={20} />} tone="var(--bad)" />
          <Stat label="Net" value={money(data.summary.net)} icon={<WalletIcon size={20} />} tone="var(--brand)" />
          <Stat label="Entries" value={total.toLocaleString('en-IN')} icon={<LedgerIcon size={20} />} tone="var(--info)" />
        </section>
      )}

      <Card flush>
        <Tabs
          value={key}
          onChange={(next) => {
            navigate(next === 'ledger' ? '/admin/ledger' : `/admin/${next}`)
            setPage(1)
          }}
          tabs={[
            { key: 'ledger', label: 'All entries' },
            { key: 'recharges', label: 'Recharges' },
            { key: 'commission', label: 'Commission' },
          ]}
        />

        {error && (
          <Empty error title="Could not load the ledger">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<LedgerIcon size={24} />} title={`No ${config.title.toLowerCase()} yet`}>
            Wallet movements appear here as merchants top up and orders settle.
          </Empty>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Merchant</th>
                    <th>Kind</th>
                    <th>Reference</th>
                    <th className="right">Amount</th>
                    <th>Status</th>
                    <th className="right">Balance after</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        {dateTime(entry.at).split(',')[0]}
                        <span className="table__sub">{ago(entry.at)}</span>
                      </td>
                      <td>{entry.merchant?.name ?? <span className="faint">—</span>}</td>
                      <td>{humanise(entry.kind)}</td>
                      <td>
                        <code>{entry.reference}</code>
                        {entry.description && <span className="table__sub">{entry.description}</span>}
                      </td>
                      <td className="right num" style={{ color: entry.type === 'credit' ? 'var(--ok)' : 'var(--bad)' }}>
                        {entry.type === 'credit' ? '+ ' : '− '}
                        {moneyExact(entry.amount, entry.currency)}
                      </td>
                      <td>
                        <Badge tone={entry.status === 'settled' ? 'ok' : 'accent'}>{humanise(entry.status)}</Badge>
                      </td>
                      <td className="right num">{moneyExact(entry.balanceAfter, entry.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="entries"
            />
          </>
        )}
      </Card>
    </>
  )
}

// ── Platform analytics ──────────────────────────────────────────────────────

export function AdminAnalyticsPage(): React.ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<PlatformOverview>('/v1/admin/overview'),
  })

  const merchants = useQuery({
    queryKey: ['admin', 'merchants', '', 'all', 1],
    queryFn: () => api<Paged<AdminMerchant>>('/v1/admin/merchants', { query: { page: 1, perPage: 50 } }),
  })

  const totalGmv = merchants.data?.items.reduce((sum, merchant) => sum + Number(merchant.gmv), 0) ?? 0

  return (
    <>
      <PageHead title="Platform analytics" subtitle="Growth, volume and concentration across every merchant." />

      {error && (
        <Card>
          <Empty error title="Could not load platform analytics">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton count={4} />}

      {data && (
        <section className="grid grid--4">
          <Stat label={`GMV (${data.windowDays}d)`} value={money(data.gmv)} icon={<RupeeIcon size={20} />} tone="var(--brand)" />
          <Stat label="Commission" value={money(data.commission)} icon={<TrendUpIcon size={20} />} tone="var(--ok)" />
          <Stat
            label="Take rate"
            value={data.takeRate === null ? '—' : percent(data.takeRate, 2)}
            icon={<LedgerIcon size={20} />}
            tone="var(--accent)"
          />
          <Stat label="Settled orders" value={data.orders.toLocaleString('en-IN')} icon={<BagIcon size={20} />} tone="var(--warn)" />
        </section>
      )}

      <Card title="Merchant concentration" subtitle="Share of GMV by merchant" flush>
        {merchants.isPending && <RowsSkeleton rows={5} />}

        {merchants.data && totalGmv === 0 && (
          <Empty icon={<BuildingIcon size={22} />} title="No settled volume yet">
            Concentration is computed from settled orders — there are none in this window.
          </Empty>
        )}

        {merchants.data && totalGmv > 0 && (
          <ul className="list">
            {[...merchants.data.items]
              .filter((merchant) => Number(merchant.gmv) > 0)
              .sort((a, b) => Number(b.gmv) - Number(a.gmv))
              .map((merchant) => {
                const share = (Number(merchant.gmv) / totalGmv) * 100
                return (
                  <li key={merchant.id}>
                    <span className="list__text">
                      <strong>{merchant.name}</strong>
                      <span className="meter" style={{ marginTop: 6 }}>
                        <span style={{ width: `${share}%` }} />
                      </span>
                    </span>
                    <span className="list__value">{percent(share)}</span>
                  </li>
                )
              })}
          </ul>
        )}
      </Card>
    </>
  )
}

// ── Stores and orders ───────────────────────────────────────────────────────

interface AdminStore {
  id: string
  domain: string
  name: string
  platform: string
  currency: string
  merchant: { id: string; name: string; status: string } | null
  status: 'connected' | 'pending' | 'uninstalled'
  pluginVersion: string | null
  wpVersion: string | null
  wooVersion: string | null
  connectedAt: string | null
  uninstalledAt: string | null
  lastSeenAt: string | null
  settingsPulledAt: string | null
  revenue: string
  orders: number
}

/**
 * Every storefront on the platform, whoever owns it.
 *
 * Stood as a "not available yet" placeholder until `/v1/admin/stores` shipped —
 * the merchant dashboard could show a merchant their own stores, and nothing
 * could answer "which account owns shop.example", which is how a support
 * request actually arrives.
 *
 * `Last seen` is the column worth reading first. A WooCommerce store writes it
 * on every settings pull, roughly hourly, so a store last seen yesterday has
 * stopped talking to us — a different problem from one that never finished
 * connecting, and indistinguishable from it without this.
 */
export function AdminStoresPage(): React.ReactNode {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'connected' | 'pending' | 'uninstalled'>('all')
  const [page, setPage] = useState(1)
  const [detaching, setDetaching] = useState<AdminStore | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'stores', search, status, page],
    queryFn: () =>
      api<Paged<AdminStore> & { windowDays: number }>('/v1/admin/stores', {
        query: { q: search, status: status === 'all' ? '' : status, page, perPage: PER_PAGE },
      }),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  const total = data?.total ?? 0

  return (
    <>
      <PageHead title="Stores" subtitle="Every storefront connected to the platform." />

      <Card flush>
        <Tabs
          value={status}
          onChange={(key) => reset(() => setStatus(key))}
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'connected', label: 'Connected' },
            { key: 'pending', label: 'Pending' },
            { key: 'uninstalled', label: 'Uninstalled' },
          ]}
        />

        <div className="toolbar">
          <SearchInput value={search} onChange={(next) => reset(() => setSearch(next))} placeholder="Domain, store or merchant" />
        </div>

        {error && (
          <Empty error title="Could not load stores">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<StoreIcon size={24} />} title="No stores match those filters" />
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>Merchant</th>
                    <th>Platform</th>
                    <th>Version</th>
                    <th>Last seen</th>
                    <th className="right">Revenue ({data.windowDays}d)</th>
                    <th className="right">Orders</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((store) => (
                    <tr key={store.id}>
                      <td>
                        <span className="table__cell">
                          <span>
                            {store.name}
                            <span className="table__sub">{store.domain}</span>
                          </span>
                        </span>
                      </td>
                      <td>
                        {store.merchant ? (
                          store.merchant.name
                        ) : (
                          // A store with no merchant is not cosmetic: every
                          // merchant-scoped screen resolves through that id, so
                          // this one is invisible to the person who owns it.
                          <Badge tone="warn">Unassigned</Badge>
                        )}
                      </td>
                      <td>{humanise(store.platform)}</td>
                      <td className="num">{store.pluginVersion ?? '—'}</td>
                      <td>{store.lastSeenAt ? ago(store.lastSeenAt) : '—'}</td>
                      <td className="right num">{money(store.revenue, store.currency)}</td>
                      <td className="right num">{store.orders.toLocaleString('en-IN')}</td>
                      <td>
                        <Badge tone={store.status === 'connected' ? 'ok' : store.status === 'pending' ? 'warn' : undefined}>
                          {humanise(store.status)}
                        </Badge>
                      </td>
                      <td>
                        {/* Only where there is something to detach. On an
                            already-unassigned row the action is a no-op, and an
                            enabled control that does nothing reads as broken. */}
                        {store.merchant && (
                          <Menu label={`Actions for ${store.domain}`} trigger={<MoreIcon size={16} />}>
                            {(close) => (
                              <MenuItem
                                danger
                                icon={<PlugIcon size={16} />}
                                onClick={() => {
                                  close()
                                  setDetaching(store)
                                }}
                              >
                                Detach from merchant
                              </MenuItem>
                            )}
                          </Menu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="stores"
            />
          </>
        )}
      </Card>

      {detaching && <DetachStoreDrawer store={detaching} onClose={() => setDetaching(null)} />}
    </>
  )
}

/**
 * Free a domain that is stuck on the wrong account.
 *
 * The case this exists for: a merchant connected their store under a test
 * account, or their account was replaced, and now every install attempt comes
 * back "already connected to a different Outfalak Checkout account" with no way
 * through it. Before this the fix was an UPDATE typed into production by hand.
 *
 * It detaches; it does not delete. Deleting the store row cascades through
 * orders, their payment records, and the account rows themselves — so the
 * endpoint behind this has no delete to call, and this panel says so plainly
 * rather than leaving the operator to wonder where that button is.
 *
 * The domain has to be typed back. The list is paged and sorted by id, so the
 * row under the cursor moves between renders, and freeing the wrong store takes
 * a live storefront off the dashboard of whoever is selling through it.
 */
function DetachStoreDrawer({ store, onClose }: { store: AdminStore; onClose: () => void }): React.ReactNode {
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')

  const detach = useMutation({
    mutationFn: () =>
      api<{ detached: boolean; previous: { merchantName: string | null } | null; kept: { orders: number; customers: number } | null }>(
        `/v1/admin/stores/${store.id}/detach`,
        { method: 'POST', body: { domain: typed.trim(), reason: reason.trim() || undefined } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
    },
  })

  const matches = typed.trim() === store.domain
  const done = detach.data

  return (
    <Drawer
      open
      onClose={onClose}
      title="Detach store"
      subtitle={store.domain}
      footer={
        done ? (
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!matches || detach.isPending}
              onClick={() => detach.mutate()}
            >
              {detach.isPending ? 'Detaching…' : 'Detach store'}
            </button>
          </>
        )
      }
    >
      {done ? (
        <Notice kind="ok" title="Detached">
          {store.domain} is free to connect to any account.
          {done.kept && (
            <>
              {' '}
              Its {done.kept.orders.toLocaleString('en-IN')} order{done.kept.orders === 1 ? '' : 's'} and{' '}
              {done.kept.customers.toLocaleString('en-IN')} customer{done.kept.customers === 1 ? '' : 's'} stayed on the
              store and will follow it to whoever installs next.
            </>
          )}
        </Notice>
      ) : (
        <>
          {detach.error && (
            <Notice kind="error" title="Could not detach">
              {(detach.error as Error).message}
            </Notice>
          )}

          <Notice kind="warn" title={`This releases the store from ${store.merchant?.name ?? 'its merchant'}`}>
            The store disappears from that account&rsquo;s dashboard. Nothing is deleted — orders, customers and
            settings stay on the store, and the next account to install it inherits them.
          </Notice>

          <DrawerSection title="Store">
            <KeyValue
              rows={[
                { label: 'Domain', value: store.domain },
                { label: 'Merchant', value: store.merchant?.name ?? '—' },
                { label: 'Platform', value: humanise(store.platform) },
                { label: 'Status', value: <Badge tone={store.status === 'connected' ? 'ok' : undefined}>{humanise(store.status)}</Badge> },
                { label: 'Orders', value: store.orders.toLocaleString('en-IN') },
              ]}
            />
          </DrawerSection>

          <DrawerSection title="Confirm">
            <Field label="Type the domain to confirm" hint={store.domain}>
              <input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                placeholder={store.domain}
                autoComplete="off"
                spellCheck={false}
              />
            </Field>

            <Field label="Reason" hint="Optional. Stored on the store's event log alongside who detached it.">
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Connected under the wrong account"
                maxLength={200}
              />
            </Field>
          </DrawerSection>
        </>
      )}
    </Drawer>
  )
}

interface AdminOrder {
  id: string
  orderNumber: string
  phone: string | null
  email: string | null
  customerName: string | null
  total: string
  currency: string
  paymentMethod: string | null
  paymentStatus: string
  orderStatus: string
  placedAt: string
  commission: string
  commissionPaidBy: string | null
  products: ProductLine[]
  store: {
    id: string
    domain: string
    platform: string
    merchant: { id: string; name: string } | null
  } | null
}

/**
 * Order search across every store on the platform.
 *
 * Stood as a placeholder until `/v1/admin/orders` shipped. The merchant list is
 * scoped by `requireShopId`, which is right there and useless here: a support
 * message arrives as an order number and a phone, and finding it meant guessing
 * whose store it belonged to first.
 *
 * Read-only by design. Cancelling and refunding stay on the merchant's own
 * screens, where the person pressing the button is the one accountable for it.
 */
export function AdminOrdersPage(): React.ReactNode {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'paid' | 'cod_pending' | 'pending' | 'failed' | 'refunded'>('all')
  const [page, setPage] = useState(1)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'orders', search, status, page],
    queryFn: () =>
      api<Paged<AdminOrder>>('/v1/admin/orders', {
        query: { q: search, paymentStatus: status === 'all' ? '' : status, page, perPage: PER_PAGE },
      }),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  const total = data?.total ?? 0

  return (
    <>
      <PageHead title="Orders" subtitle="Platform-wide order search, for support and reconciliation." />

      <Card flush>
        <Tabs
          value={status}
          onChange={(key) => reset(() => setStatus(key))}
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'paid', label: 'Paid' },
            { key: 'cod_pending', label: 'COD' },
            { key: 'pending', label: 'Pending' },
            { key: 'failed', label: 'Failed' },
            { key: 'refunded', label: 'Refunded' },
          ]}
        />

        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={(next) => reset(() => setSearch(next))}
            placeholder="Order number, phone, email or domain"
          />
        </div>

        {error && (
          <Empty error title="Could not load orders">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<BagIcon size={24} />} title="No orders match those filters" />
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Store</th>
                    <th>Merchant</th>
                    <th>Placed</th>
                    <th>Method</th>
                    <th className="right">Total</th>
                    <th className="right">Commission</th>
                    <th>Payment</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <span className="table__cell">
                          <span>
                            {order.orderNumber}
                            <span className="table__sub">{order.customerName ?? order.phone ?? order.email ?? '—'}</span>
                            <ProductLines products={order.products} />
                          </span>
                        </span>
                      </td>
                      <td>{order.store?.domain ?? '—'}</td>
                      <td>{order.store?.merchant?.name ?? <Badge tone="warn">Unassigned</Badge>}</td>
                      <td>{ago(order.placedAt)}</td>
                      <td>{order.paymentMethod ? humanise(order.paymentMethod) : '—'}</td>
                      <td className="right num">{money(order.total, order.currency)}</td>
                      <td className="right num">
                        {Number(order.commission) > 0 ? (
                          <>
                            {money(order.commission, order.currency)}
                            <span className="table__sub">
                              {order.commissionPaidBy === 'customer' ? 'customer pays' : 'merchant pays'}
                            </span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <Badge tone={order.paymentStatus}>{humanise(order.paymentStatus)}</Badge>
                      </td>
                      <td>
                        {order.orderStatus !== 'cancelled' &&
                          order.paymentStatus !== 'paid' &&
                          order.paymentStatus !== 'cod_pending' && <ForceChargeButton order={order} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil(total / PER_PAGE))}
              total={total}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="orders"
            />
          </>
        )}
      </Card>
    </>
  )
}

/**
 * Manually confirms one order the storefront's own sync left unpaid, and
 * charges its commission at the merchant's rate today.
 *
 * Built for the WooCommerce plugin's own gap — it mirrors an order once, at
 * creation, and never re-syncs it when the merchant later marks it
 * "Processing"/"Completed" in wp-admin — so a COD sale the merchant has
 * actually confirmed sits here forever at `pending`, its commission never
 * charged. An operator who has confirmed the sale some other way uses this
 * instead of waiting on that fix.
 */
function ForceChargeButton({ order }: { order: AdminOrder }): React.ReactNode {
  const queryClient = useQueryClient()

  const charge = useMutation({
    mutationFn: () =>
      api<{ charged: boolean; duplicate: boolean; reason?: string; amount?: string }>(
        `/v1/admin/orders/${order.id}/force-charge`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] })
    },
  })

  if (charge.isSuccess) {
    return charge.data.charged ? (
      <Badge tone="ok">Charged {money(charge.data.amount ?? '0', order.currency)}</Badge>
    ) : (
      <Badge tone="warn">{charge.data.reason ?? 'Not charged'}</Badge>
    )
  }

  return (
    <button
      type="button"
      className="btn btn--sm"
      disabled={charge.isPending}
      title="Confirm this sale and charge its commission at the merchant's current rate"
      onClick={() => charge.mutate()}
    >
      {charge.isPending ? 'Charging…' : 'Force charge'}
    </button>
  )
}

export function AdminHealthPage(): React.ReactNode {
  /*
   * Through `health()` rather than `api()`. That endpoint's `ok` answers "is
   * the platform healthy", not "did this request succeed", so the envelope
   * client read a degraded API as a failed request and threw its perfectly
   * good payload away — leaving this screen blank at the one moment it is the
   * screen somebody is on.
   */
  const { data, isPending } = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => health(signal),
    staleTime: 0,
  })

  return (
    <>
      <PageHead title="Health &amp; audit" subtitle="Is the platform up, and what is connected." />

      {isPending && <StatsSkeleton count={4} />}

      {data && (
        <section className="grid grid--4">
          <Stat
            label="Postgres"
            value={humanise(data.postgres)}
            icon={data.postgres === 'up' ? <CheckCircleIcon size={20} /> : <AlertIcon size={20} />}
            tone={data.postgres === 'up' ? 'var(--ok)' : 'var(--bad)'}
          />
          <Stat
            label="Redis"
            value={humanise(data.redis)}
            icon={data.redis === 'up' ? <CheckCircleIcon size={20} /> : <AlertIcon size={20} />}
            tone={data.redis === 'up' ? 'var(--ok)' : 'var(--bad)'}
          />
          <Stat
            label="Realtime connections"
            value={data.realtime.connections.toLocaleString('en-IN')}
            icon={<ShieldIcon size={20} />}
            tone="var(--brand)"
            footnote={<span className="faint">{data.realtime.channels} channels</span>}
          />
          <Stat label="API version" value={data.version ?? 'unknown'} icon={<ShieldIcon size={20} />} tone="var(--info)" />
        </section>
      )}

      <Card title="What this reads">
        <Ticks
          items={[
            'Live from the API’s own /health endpoint, not a cached status page',
            'An API that cannot be reached is shown as down rather than as a blank card',
            'Postgres and Redis are checked on every call',
            'Realtime counts are the sockets this API process is holding',
          ]}
        />
      </Card>
    </>
  )
}
// ── Admin settings ──────────────────────────────────────────────────────────

/**
 * The one screen on this console that writes to production.
 *
 * Commission is real money: it is applied per order and frozen onto the order
 * row at creation, so a change here affects every order placed after it and
 * none placed before. That shape drives the design — a worked example of what
 * the rate does to a real order, and an explicit Save rather than autosave.
 */
interface CommissionForm {
  enabled: boolean
  type: 'fixed' | 'percent'
  amount: number
  taxPercent: number
  paidBy: 'merchant' | 'customer'
  appliesToPrepaid: boolean
  appliesToCod: boolean
}


interface GatewayForm {
  enabled: boolean
  provider: string
  mode: 'sandbox' | 'production'
  appId: string
  hasAppId: boolean
  hasSecret: boolean
  recharge: { min: number; max: number; presets: string }
}

/**
 * The account merchants' wallet top-ups are paid into.
 *
 * The platform's own gateway, not a merchant's. Everything a merchant does that
 * costs money — a paid plan, a WhatsApp message, commission — is settled from
 * their wallet, and until this is configured and switched on there is no way to
 * put anything in one.
 *
 * The secret is never sent to this screen; the field is blank on load and blank
 * means "leave it alone". Rendering a live payment secret into the page would
 * put it in the browser cache and in every screenshot of it.
 */
function GatewayCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<GatewayForm | null>(null)
  const [appId, setAppId] = useState('')
  const [secret, setSecret] = useState('')
  const [saved, setSaved] = useState(false)
  const [tested, setTested] = useState<{ ok: boolean; error: string | null } | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'gateway'],
    queryFn: () => api<GatewayForm>('/v1/admin/gateway'),
  })

  const current = form ?? data ?? null

  const save = useMutation({
    mutationFn: (body: GatewayForm) =>
      api<{ saved: boolean }>('/v1/admin/gateway', {
        method: 'PUT',
        body: {
          enabled: body.enabled,
          mode: body.mode,
          appId: appId.trim() || undefined,
          secretKey: secret.trim() || undefined,
          recharge: body.recharge,
        },
      }),
    onSuccess: () => {
      setSaved(true)
      setAppId('')
      setSecret('')
      setTested(null)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gateway'] })
    },
  })

  const test = useMutation({
    mutationFn: () => api<{ ok: boolean; mode: string; error: string | null }>('/v1/admin/gateway/test', { method: 'POST' }),
    onSuccess: (result) => setTested({ ok: result.ok, error: result.error }),
    onError: (err) => setTested({ ok: false, error: (err as Error).message }),
  })

  if (isPending) {
    return (
      <Card title="Payment gateway">
        <RowsSkeleton rows={5} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="Payment gateway">
        <Empty error title="Could not load the gateway settings">
          {(error as Error | undefined)?.message ?? 'The endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  const dirty =
    (form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)) ||
    appId.trim() !== '' ||
    secret.trim() !== ''

  function set<K extends keyof GatewayForm>(key: K, value: GatewayForm[K]): void {
    setForm({ ...(current as GatewayForm), [key]: value })
  }

  const ready = (current.hasAppId || appId.trim() !== '') && (current.hasSecret || secret.trim() !== '')

  return (
    <Card
      title="Payment gateway"
      subtitle="Where merchants' wallet top-ups are paid in. Not a merchant's own gateway."
      action={
        <button type="button" className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(current)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          Merchants can top up from their wallet screen.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {current.enabled && !ready && (
        <Notice kind="warn" title="Switched on with nothing behind it">
          Top-ups are enabled but no credentials are saved, so every attempt will fail. Add them below.
        </Notice>
      )}

      <SettingRow
        title="Accept top-ups"
        description="Off means the Recharge wallet screen tells merchants to contact support instead."
      >
        <Switch checked={current.enabled} label="Accept top-ups" onChange={(next) => set('enabled', next)} />
      </SettingRow>

      <div className="grid grid--2" style={{ marginTop: 14 }}>
        <Field label="Provider" hint="Cashfree is the only gateway the top-up path is built against.">
          <input className="input" value="Cashfree" disabled />
        </Field>

        <Field label="Mode" hint="Sandbox takes no real money. Production does.">
          <select className="select" value={current.mode} onChange={(event) => set('mode', event.target.value as 'sandbox' | 'production')}>
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </Field>

        <Field label="App ID" hint={current.hasAppId ? `Saved: ${current.appId}. Type to replace it.` : 'Not set yet.'}>
          <input
            className="input"
            value={appId}
            placeholder={current.hasAppId ? current.appId : 'Cashfree app id'}
            onChange={(event) => setAppId(event.target.value)}
          />
        </Field>

        <Field
          label="Secret key"
          hint={current.hasSecret ? 'A secret is saved. Leave blank to keep it.' : 'Not set yet.'}
        >
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={secret}
            placeholder={current.hasSecret ? '••••••••••••' : 'Cashfree secret key'}
            onChange={(event) => setSecret(event.target.value)}
          />
        </Field>

        <Field label="Smallest top-up" hint="Below this the API refuses.">
          <input
            className="input"
            inputMode="decimal"
            value={String(current.recharge.min)}
            onChange={(event) =>
              set('recharge', { ...current.recharge, min: Number(event.target.value.replace(/[^\d.]/g, '')) || 0 })
            }
          />
        </Field>

        <Field label="Largest top-up">
          <input
            className="input"
            inputMode="decimal"
            value={String(current.recharge.max)}
            onChange={(event) =>
              set('recharge', { ...current.recharge, max: Number(event.target.value.replace(/[^\d.]/g, '')) || 0 })
            }
          />
        </Field>
      </div>

      <Field label="Suggested amounts" hint="Comma separated. Shown as buttons on the merchant's Recharge wallet screen.">
        <input
          className="input"
          value={current.recharge.presets}
          onChange={(event) => set('recharge', { ...current.recharge, presets: event.target.value })}
        />
      </Field>

      <div className="row" style={{ marginTop: 16, gap: 10 }}>
        <button type="button" className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
          {test.isPending ? 'Checking…' : 'Test credentials'}
        </button>

        {/* Checks the saved credentials, not what is typed above — the point is
            to test what merchants will actually be charged against. */}
        {tested && (
          <Badge tone={tested.ok ? 'ok' : 'bad'} dot>
            {tested.ok ? 'Cashfree accepted these credentials' : (tested.error ?? 'Rejected')}
          </Badge>
        )}
      </div>

      {current.mode === 'production' && (
        <Notice kind="warn" title="Production mode">
          Top-ups made here move real money into the account these credentials belong to.
        </Notice>
      )}
    </Card>
  )
}

interface OtpForm {
  provider: 'local' | 'wakit'
  defaultEnabled: boolean
  rememberDays: number
  wakitBaseUrl: string
  hasWakitKey: boolean
  wakitNumberId: string
  deliverable: boolean
}

interface WakitNumbers {
  reachable: boolean
  error: string | null
  numbers: { id: string; number: string; name: string | null; canSend: boolean; isDefault: boolean; status: string }[]
}

/**
 * OTP delivery through Wakit.
 *
 * Platform settings: the dashboard's phone sign-in reads them, and every shop's
 * checkout inherits them. The API key is write-only, as on the gateway card.
 */
function OtpCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<OtpForm | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [saved, setSaved] = useState(false)
  const [testPhone, setTestPhone] = useState('')
  const [tested, setTested] = useState<{ ok: boolean; error: string | null } | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'otp'],
    queryFn: () => api<OtpForm>('/v1/admin/otp'),
  })

  const current = form ?? data ?? null

  const save = useMutation({
    mutationFn: (body: OtpForm) =>
      api<{ saved: boolean }>('/v1/admin/otp', {
        method: 'PUT',
        body: {
          defaultEnabled: body.defaultEnabled,
          rememberDays: body.rememberDays,
          wakitBaseUrl: body.wakitBaseUrl,
          wakitApiKey: apiKey.trim() || undefined,
          wakitNumberId: body.wakitNumberId,
        },
      }),
    onSuccess: () => {
      setSaved(true)
      setForm(null)
      setApiKey('')
      setTested(null)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'otp'] })
    },
  })

  // Live from Wakit, so a number connected there appears without a re-save.
  const numbers = useQuery({
    queryKey: ['admin', 'otp', 'numbers'],
    queryFn: () => api<WakitNumbers>('/v1/admin/otp/numbers'),
    enabled: Boolean(data?.hasWakitKey),
  })

  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error: string | null }>('/v1/admin/otp/test', { method: 'POST', body: { phone: testPhone } }),
    onSuccess: (result) => setTested({ ok: result.ok, error: result.error }),
    onError: (err) => setTested({ ok: false, error: (err as Error).message }),
  })

  if (isPending) {
    return (
      <Card title="OTP verification">
        <RowsSkeleton rows={4} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="OTP verification">
        <Empty error title="Could not load the OTP settings">
          {(error as Error | undefined)?.message ?? 'The endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  // Saving always selects Wakit, so a store still on the old local provider
  // counts as a change worth saving.
  const dirty =
    (form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)) ||
    apiKey.trim() !== '' ||
    current.provider !== 'wakit'

  function set<K extends keyof OtpForm>(key: K, value: OtpForm[K]): void {
    setForm({ ...(current as OtpForm), [key]: value })
  }

  const connected = current.provider === 'wakit' && current.deliverable

  return (
    <Card
      title="OTP verification"
      subtitle="Checkout codes for every store, sent through Outfalak's Wakit account."
      action={
        <button type="button" className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(current)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          OTPs now go out through Wakit.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {!connected && (
        <Notice kind="warn" title="Wakit is not connected">
          Add the Wakit base URL and API key below and save. Until then phone sign-in is refused in production.
        </Notice>
      )}

      <div className="row" style={{ gap: 10, marginBottom: 12 }}>
        <Badge tone={connected ? 'ok' : 'warn'} dot>
          {connected ? 'Connected to Wakit' : 'Not connected'}
        </Badge>
      </div>

      <div className="grid grid--2">
        <Field label="Wakit base URL" hint="For example https://api.wakit.in">
          <input
            className="input"
            value={current.wakitBaseUrl}
            placeholder="https://"
            onChange={(event) => set('wakitBaseUrl', event.target.value)}
          />
        </Field>

        <Field label="Wakit API key" hint={current.hasWakitKey ? 'A key is saved. Leave blank to keep it.' : 'Not set yet.'}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={apiKey}
            placeholder={current.hasWakitKey ? '••••••••••••' : 'Wakit API key'}
            onChange={(event) => setApiKey(event.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Send OTPs from"
        hint={
          numbers.data && !numbers.data.reachable
            ? (numbers.data.error ?? 'Could not read the numbers from Wakit.')
            : 'The numbers connected in this Wakit account. Connect more in Wakit.'
        }
      >
        <select
          className="select"
          value={current.wakitNumberId}
          disabled={!current.hasWakitKey || numbers.isPending}
          onChange={(event) => set('wakitNumberId', event.target.value)}
        >
          <option value="">Wakit default (newest connected number)</option>
          {numbers.data?.numbers.map((row) => (
            <option key={row.id} value={row.id} disabled={!row.canSend}>
              {row.number}
              {row.name ? ` — ${row.name}` : ''}
              {row.canSend ? '' : ` (${row.status || 'cannot send'})`}
            </option>
          ))}
          {/* A saved number Wakit no longer lists stays visible, so saving does not silently change it. */}
          {current.wakitNumberId !== '' && numbers.data && !numbers.data.numbers.some((row) => row.id === current.wakitNumberId) && (
            <option value={current.wakitNumberId}>Number #{current.wakitNumberId} (not found in Wakit)</option>
          )}
        </select>
      </Field>

      <SettingRow
        title="Require OTP at checkout"
        description="Applies to every store. Merchants cannot change this."
      >
        <Switch checked={current.defaultEnabled} label="Require OTP at checkout" onChange={(next) => set('defaultEnabled', next)} />
      </SettingRow>

      <Field label="Remember a verified number for (days)" hint="0 asks for a code on every order.">
        <input
          className="input"
          inputMode="numeric"
          value={String(current.rememberDays)}
          onChange={(event) => set('rememberDays', Math.min(365, Number(event.target.value.replace(/\D/g, '')) || 0))}
        />
      </Field>

      {/* Sends through the saved settings, not what is typed above. */}
      <div className="row" style={{ marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ maxWidth: 220 }}
          inputMode="tel"
          placeholder="Mobile number"
          value={testPhone}
          onChange={(event) => setTestPhone(event.target.value)}
        />
        <button type="button" className="btn" disabled={test.isPending || testPhone.trim() === ''} onClick={() => test.mutate()}>
          {test.isPending ? 'Sending…' : 'Send test OTP'}
        </button>

        {tested && (
          <Badge tone={tested.ok ? 'ok' : 'bad'} dot>
            {tested.ok ? 'Sent — check the phone' : (tested.error ?? 'Could not send')}
          </Badge>
        )}
      </div>
    </Card>
  )
}

interface GoogleForm {
  enabled: boolean
  clientId: string
  hasSecret: boolean
  callbackUrl: string
  origin: string
}

/**
 * Google sign-in for the merchant dashboard.
 *
 * Shows the two URLs Google's console asks for, so they are copied rather than
 * typed. The client secret is write-only.
 */
function GoogleCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<GoogleForm | null>(null)
  const [secret, setSecret] = useState('')
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'google'],
    queryFn: () => api<GoogleForm>('/v1/admin/google'),
  })

  const current = form ?? data ?? null

  const save = useMutation({
    mutationFn: (body: GoogleForm) =>
      api<{ saved: boolean }>('/v1/admin/google', {
        method: 'PUT',
        body: { enabled: body.enabled, clientId: body.clientId, clientSecret: secret.trim() || undefined },
      }),
    onSuccess: () => {
      setSaved(true)
      setForm(null)
      setSecret('')
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'google'] })
    },
  })

  if (isPending) {
    return (
      <Card title="Google sign-in">
        <RowsSkeleton rows={4} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="Google sign-in">
        <Empty error title="Could not load the Google settings">
          {(error as Error | undefined)?.message ?? 'The endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  const dirty =
    (form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)) || secret.trim() !== ''

  function set<K extends keyof GoogleForm>(key: K, value: GoogleForm[K]): void {
    setForm({ ...(current as GoogleForm), [key]: value })
  }

  const ready = current.clientId.trim() !== '' && (current.hasSecret || secret.trim() !== '')

  return (
    <Card
      title="Google sign-in"
      subtitle="Lets merchants sign in to the dashboard with their Google account."
      action={
        <button type="button" className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(current)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          {current.enabled ? 'The Google button on the sign-in page is live.' : 'Google sign-in is off.'}
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      <SettingRow title="Allow Google sign-in" description="Off shows merchants a message instead of sending them to Google.">
        <Switch
          checked={current.enabled}
          label="Allow Google sign-in"
          disabled={!ready && !current.enabled}
          onChange={(next) => set('enabled', next)}
        />
      </SettingRow>

      <div className="grid grid--2" style={{ marginTop: 14 }}>
        <Field label="Client ID" hint="From Google Cloud → APIs & Services → Credentials.">
          <input
            className="input"
            value={current.clientId}
            placeholder="xxxxxxxx.apps.googleusercontent.com"
            onChange={(event) => set('clientId', event.target.value)}
          />
        </Field>

        <Field label="Client secret" hint={current.hasSecret ? 'A secret is saved. Leave blank to keep it.' : 'Not set yet.'}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={secret}
            placeholder={current.hasSecret ? '••••••••••••' : 'Client secret'}
            onChange={(event) => setSecret(event.target.value)}
          />
        </Field>

        <Field label="Authorised JavaScript origin" hint="Paste into Google's console.">
          <input className="input" value={current.origin} readOnly onFocus={(event) => event.target.select()} />
        </Field>

        <Field label="Authorised redirect URI" hint="Paste into Google's console, exactly as shown.">
          <input className="input" value={current.callbackUrl} readOnly onFocus={(event) => event.target.select()} />
        </Field>
      </div>
    </Card>
  )
}

/**
 * Per-plan order fee, in percent per plan.
 *
 * A rate above zero is what merchants on that plan are charged per settled
 * order instead of the platform commission — debited from their wallet, with
 * GST on top at the commission's tax rate. Zero falls back to the platform
 * commission. Frozen onto each order at creation, so past orders never move.
 */
interface PlanChargesForm {
  starter: number
  professional: number
  scale: number
}

const PLAN_CHARGE_LABELS: Array<{ key: keyof PlanChargesForm; name: string }> = [
  { key: 'starter', name: 'Starter' },
  { key: 'professional', name: 'Professional' },
  { key: 'scale', name: 'Scale' },
]

/**
 * A decimal input that does not eat the decimal point while it is typed.
 *
 * A controlled input bound straight to a number re-renders every keystroke
 * through `String(Number(...))`: typing "0." parses to 0 and snaps back to
 * "0", so 0.5 — or any fraction — can never be entered at all. The raw
 * keystrokes live here until blur; the parsed number is committed on every
 * change so the preview and the dirty check stay live.
 */
function DecimalInput({
  value,
  onNumber,
  disabled,
}: {
  value: number
  onNumber: (next: number) => void
  disabled?: boolean
}): React.ReactNode {
  const [raw, setRaw] = useState<string | null>(null)

  return (
    <input
      className="input"
      inputMode="decimal"
      value={raw ?? String(value)}
      disabled={disabled}
      onChange={(event) => {
        const text = event.target.value
        setRaw(text)
        const parsed = Number(text.replace(/[^\d.]/g, ''))
        onNumber(Number.isFinite(parsed) ? parsed : 0)
      }}
      onBlur={() => setRaw(null)}
    />
  )
}

function PlanChargesCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<PlanChargesForm | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'plan-charges'],
    queryFn: () => api<PlanChargesForm>('/v1/admin/plan-charges'),
  })

  const save = useMutation({
    mutationFn: (body: PlanChargesForm) => api<PlanChargesForm>('/v1/admin/plan-charges', { method: 'PUT', body }),
    onSuccess: (next) => {
      setForm(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'plan-charges'] })
      // The merchant-facing billing screen reads the same setting.
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
    },
  })

  // The server's answer is the starting point; edits live in `form` until saved,
  // so an in-flight refetch cannot overwrite what is being typed.
  const current = form ?? data ?? null
  const dirty = form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)

  function set(key: keyof PlanChargesForm, value: number): void {
    setForm({ ...(current as PlanChargesForm), [key]: Math.min(100, Math.max(0, value || 0)) })
  }

  if (isPending) {
    return (
      <Card title="Plan charges" subtitle="Per-plan order fee.">
        <RowsSkeleton rows={3} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="Plan charges" subtitle="Per-plan order fee.">
        <Empty error title="Could not load plan charges">
          {(error as Error | undefined)?.message ?? 'The settings endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  return (
    <Card
      title="Plan charges"
      subtitle="Order fee per plan, in percent. Debited from the wallet on every settled order."
      action={
        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(current)}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          The new rates apply to orders placed from now on.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      <div className="grid grid--3">
        {PLAN_CHARGE_LABELS.map(({ key, name }) => (
          <Field
            key={key}
            label={`${name} (%)`}
            hint={current[key] > 0 ? `On a ₹1,000 order: ${moneyExact((1000 * current[key]) / 100)} + GST.` : 'Falls back to the platform commission.'}
          >
            <DecimalInput value={current[key]} onNumber={(next) => set(key, next)} />
          </Field>
        ))}
      </div>
    </Card>
  )
}

interface SmtpForm {
  host: string
  port: number
  secure: boolean
  user: string
  hasPassword: boolean
  fromAddress: string
  fromName: string
  configured: boolean
}

/**
 * The SMTP account the platform's own mail goes out through.
 *
 * Credentials live in platform settings rather than only the deployment
 * environment, so connecting a mail account is a save here rather than a
 * redeploy — the SMTP_* env vars remain the fallback for deployments
 * configured before this screen existed. The password is write-only, exactly
 * like the API keys on the other cards on this page.
 */
function SmtpCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<SmtpForm | null>(null)
  const [password, setPassword] = useState('')
  const [saved, setSaved] = useState(false)
  const [testTo, setTestTo] = useState('')
  const [tested, setTested] = useState<{ ok: boolean; error: string | null } | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'smtp'],
    queryFn: () => api<SmtpForm>('/v1/admin/smtp'),
  })

  const current = form ?? data ?? null

  const save = useMutation({
    mutationFn: (body: SmtpForm) =>
      api<{ saved: boolean }>('/v1/admin/smtp', {
        method: 'PUT',
        body: {
          host: body.host.trim(),
          port: body.port,
          secure: body.secure,
          user: body.user.trim(),
          pass: password.trim() || undefined,
          fromAddress: body.fromAddress.trim(),
          fromName: body.fromName.trim(),
        },
      }),
    onSuccess: () => {
      setSaved(true)
      setForm(null)
      setPassword('')
      setTested(null)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'smtp'] })
    },
  })

  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error: string | null }>('/v1/admin/smtp/test', { method: 'POST', body: { to: testTo.trim() } }),
    onSuccess: (result) => setTested({ ok: result.ok, error: result.error }),
    onError: (err) => setTested({ ok: false, error: (err as Error).message }),
  })

  if (isPending) {
    return (
      <Card title="SMTP">
        <RowsSkeleton rows={5} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="SMTP">
        <Empty error title="Could not load the SMTP settings">
          {(error as Error | undefined)?.message ?? 'The endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  const dirty =
    (form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)) || password.trim() !== ''

  function set<K extends keyof SmtpForm>(key: K, value: SmtpForm[K]): void {
    setForm({ ...(current as SmtpForm), [key]: value })
  }

  return (
    <Card
      title="SMTP"
      subtitle="The mail account the platform's own mail is sent from — download links, test mail."
      action={
        <button type="button" className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(current)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          Mail from the platform now goes out through this account.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {!current.configured && (
        <Notice kind="warn" title="SMTP is not connected">
          No host is saved, so nothing is mailed and the email endpoints answer 503. Save a host below to connect the platform's sender.
        </Notice>
      )}

      <div className="row" style={{ gap: 10, marginBottom: 12 }}>
        <Badge tone={current.configured ? 'ok' : 'warn'} dot>
          {current.configured ? `Connected to ${current.host}` : 'Not connected'}
        </Badge>
      </div>

      <div className="grid grid--2">
        <Field label="Host" hint="For example smtp.gmail.com or mail.outfalakcheckout.com">
          <input
            className="input"
            value={current.host}
            placeholder="smtp.example.com"
            onChange={(event) => set('host', event.target.value)}
          />
        </Field>

        <Field label="Port" hint="587 with STARTTLS, or 465 with TLS from the start.">
          <input
            className="input"
            inputMode="numeric"
            value={String(current.port)}
            onChange={(event) => set('port', Math.min(65535, Number(event.target.value.replace(/\D/g, '')) || 0))}
          />
        </Field>

        <Field label="Username" hint="Left blank when the server needs no login.">
          <input
            className="input"
            value={current.user}
            autoComplete="off"
            onChange={(event) => set('user', event.target.value)}
          />
        </Field>

        <Field label="Password" hint={current.hasPassword ? 'A password is saved. Leave blank to keep it.' : 'Not set yet.'}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            placeholder={current.hasPassword ? '••••••••••••' : 'SMTP password'}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label="From address" hint="The address every platform mail is sent as.">
          <input
            className="input"
            value={current.fromAddress}
            placeholder="noreply@outfalakcheckout.com"
            onChange={(event) => set('fromAddress', event.target.value)}
          />
        </Field>

        <Field label="From name" hint="Shown beside the from address in the inbox.">
          <input
            className="input"
            value={current.fromName}
            placeholder="Outfalak Checkout"
            onChange={(event) => set('fromName', event.target.value)}
          />
        </Field>
      </div>

      <SettingRow
        title="TLS from the start"
        description="On for port 465, where the connection is TLS before the greeting. Off for 587, where TLS starts after it (STARTTLS)."
      >
        <Switch checked={current.secure} label="TLS from the start" onChange={(next) => set('secure', next)} />
      </SettingRow>

      {/* Sends through the saved settings, not what is typed above — so the
          button waits for a save when the form is dirty. */}
      <div className="row" style={{ marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ maxWidth: 260 }}
          type="email"
          placeholder="Your own email address"
          value={testTo}
          onChange={(event) => setTestTo(event.target.value)}
        />
        <button
          type="button"
          className="btn"
          disabled={test.isPending || !current.configured || dirty || testTo.trim() === ''}
          onClick={() => test.mutate()}
        >
          {test.isPending ? 'Sending…' : 'Send test email'}
        </button>

        {tested && (
          <Badge tone={tested.ok ? 'ok' : 'bad'} dot>
            {tested.ok ? 'Sent — check the inbox' : (tested.error ?? 'Could not send')}
          </Badge>
        )}

        {dirty && current.configured && (
          <span className="muted" style={{ fontSize: '.82rem' }}>
            Save first — the test goes out through the saved account.
          </span>
        )}
      </div>
    </Card>
  )
}

/**
 * Platform-sent order email.
 *
 * The sender itself is the SMTP card above — this decides only whether mail
 * goes out and what it costs. Today the only mail is the download-links mail
 * for digital orders, posted by the WooCommerce plugin on settlement.
 */
interface EmailForm {
  enabled: boolean
  downloadEnabled: boolean
  downloadPrice: number
}

function EmailCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<EmailForm | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'email'],
    queryFn: () => api<EmailForm>('/v1/admin/email'),
  })

  const save = useMutation({
    mutationFn: (body: EmailForm) => api<EmailForm>('/v1/admin/email', { method: 'PUT', body }),
    onSuccess: (next) => {
      setForm(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'email'] })
    },
  })

  const current = form ?? data ?? null
  const dirty = form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)

  function set<K extends keyof EmailForm>(key: K, value: EmailForm[K]): void {
    setForm({ ...(current as EmailForm), [key]: value })
  }

  if (isPending) {
    return (
      <Card title="Email" subtitle="Order mail sent by the platform.">
        <RowsSkeleton rows={3} />
      </Card>
    )
  }

  if (error || !current) {
    return (
      <Card title="Email" subtitle="Order mail sent by the platform.">
        <Empty error title="Could not load email settings">
          {(error as Error | undefined)?.message ?? 'The settings endpoint did not answer.'}
        </Empty>
      </Card>
    )
  }

  return (
    <Card
      title="Email"
      subtitle="Download links mailed from the platform, not the merchant's hosting."
      action={
        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(current)}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          The new email settings apply to orders placed from now on.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      <SettingRow
        title="Send order email"
        description={
          current.enabled
            ? 'Platform mail is on for stores that opt in.'
            : 'Nothing is mailed. Digital orders rely on the store\u2019s own mail.'
        }
      >
        <Switch checked={current.enabled} label="Send order email" onChange={(next) => set('enabled', next)} />
      </SettingRow>

      <SettingRow
        title="Download links mail"
        description="Mailed when a WooCommerce digital order settles, with the file links the store granted."
      >
        <Switch
          checked={current.downloadEnabled}
          label="Download links mail"
          disabled={!current.enabled}
          onChange={(next) => set('downloadEnabled', next)}
        />
      </SettingRow>

      <Field label="Price per mail (₹)" hint="Debited from the merchant wallet on every mail actually sent. Zero sends free.">
        <DecimalInput value={current.downloadPrice} onNumber={(next) => set('downloadPrice', next)} />
      </Field>
    </Card>
  )
}

/** How the platform collects money. Each card loads and saves itself. */
export function AdminPaymentsPage(): React.ReactNode {
  return (
    <>
      <PageHead title="Payments" subtitle="The gateway the platform collects through." />
      <GatewayCard />
    </>
  )
}

/** The third-party services the platform talks to. */
export function AdminIntegrationsPage(): React.ReactNode {
  return (
    <>
      <PageHead title="Integrations" subtitle="Sign-in, OTP and email providers used across the platform." />
      <OtpCard />
      <GoogleCard />
      <SmtpCard />
      <EmailCard />
    </>
  )
}

export function AdminSettingsPage(): React.ReactNode {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<CommissionForm | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'commission'],
    queryFn: () => api<CommissionForm>('/v1/admin/commission'),
  })

  const save = useMutation({
    mutationFn: (body: CommissionForm) => api<CommissionForm>('/v1/admin/commission', { method: 'PUT', body }),
    onSuccess: (next) => {
      setForm(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'commission'] })
      // The merchant-facing billing screen reads the same setting.
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
    },
  })

  // The server's answer is the starting point; edits live in `form` until saved,
  // so an in-flight refetch cannot overwrite what is being typed.
  const current = form ?? data ?? null
  const dirty = form !== null && data !== undefined && JSON.stringify(form) !== JSON.stringify(data)

  function set<K extends keyof CommissionForm>(key: K, value: CommissionForm[K]): void {
    setForm({ ...(current as CommissionForm), [key]: value })
  }

  if (isPending) {
    return (
      <>
        <PageHead title="Platform settings" subtitle="Defaults that apply to every merchant." />
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      </>
    )
  }

  if (error || !current) {
    return (
      <>
        <PageHead title="Platform settings" subtitle="Defaults that apply to every merchant." />
        <Card>
          <Empty error title="Could not load platform settings">
            {(error as Error | undefined)?.message ?? 'The settings endpoint did not answer.'}
          </Empty>
        </Card>
      </>
    )
  }

  // A fixed sample order, so the abstract rate becomes a number somebody can
  // sanity-check before saving it.
  const sample = 1000
  const charge = current.type === 'percent' ? (sample * current.amount) / 100 : current.amount
  const tax = (charge * current.taxPercent) / 100

  return (
    <>
      <PageHead
        title="Platform settings"
        subtitle="Applies to every merchant on the platform."
        actions={
          <button
            type="button"
            className="btn btn--primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(current)}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {saved && (
        <Notice kind="ok" title="Saved">
          The new commission applies to orders placed from now on.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {dirty && !save.isPending && (
        <Notice kind="warn" title="Unsaved changes">
          Nothing is charged differently until you press Save.
        </Notice>
      )}

      <div className="grid grid--main">
        <div className="stack">
          <Card title="Commission" subtitle="What the platform charges per settled order.">
            <SettingRow
              title="Charge commission"
              description={
                current.enabled
                  ? 'Commission is being charged on settled orders.'
                  : 'Nothing is being charged. Orders settle with no platform fee.'
              }
            >
              <Switch checked={current.enabled} label="Charge commission" onChange={(next) => set('enabled', next)} />
            </SettingRow>

            <div className="grid grid--2" style={{ marginTop: 16, opacity: current.enabled ? 1 : 0.5 }}>
              <Field label="Charge type">
                <select
                  className="select"
                  value={current.type}
                  disabled={!current.enabled}
                  onChange={(event) => set('type', event.target.value as 'fixed' | 'percent')}
                >
                  <option value="percent">Percentage of order</option>
                  <option value="fixed">Fixed amount per order</option>
                </select>
              </Field>

              <Field
                label={current.type === 'percent' ? 'Rate (%)' : 'Amount (₹)'}
                hint={current.type === 'percent' ? 'Percentage of the order total.' : 'Flat rupees per settled order.'}
              >
                <DecimalInput
                  value={current.amount}
                  disabled={!current.enabled}
                  onNumber={(next) => set('amount', next)}
                />
              </Field>

              <Field label="GST (%)" hint="Tax on the commission itself, not on the order.">
                <DecimalInput
                  value={current.taxPercent}
                  disabled={!current.enabled}
                  onNumber={(next) => set('taxPercent', next)}
                />
              </Field>

              <Field label="Paid by" hint="Merchant: debited from their wallet. Customer: added to the order total.">
                <select
                  className="select"
                  value={current.paidBy}
                  disabled={!current.enabled}
                  onChange={(event) => set('paidBy', event.target.value as 'merchant' | 'customer')}
                >
                  <option value="merchant">Merchant wallet</option>
                  <option value="customer">Customer pays</option>
                </select>
              </Field>
            </div>
          </Card>

          <Card title="Which orders" subtitle="Turn a payment method off to exempt it entirely.">
            <SettingRow title="Prepaid orders" description="UPI, cards, netbanking and wallets.">
              <Switch
                checked={current.appliesToPrepaid}
                label="Prepaid orders"
                disabled={!current.enabled}
                onChange={(next) => set('appliesToPrepaid', next)}
              />
            </SettingRow>
            <SettingRow title="Cash on Delivery" description="COD orders, charged when they settle.">
              <Switch
                checked={current.appliesToCod}
                label="Cash on Delivery"
                disabled={!current.enabled}
                onChange={(next) => set('appliesToCod', next)}
              />
            </SettingRow>
          </Card>

          <PlanChargesCard />
        </div>

        <div className="stack">
          <Card title="On a 1,000 order" accent>
            {current.enabled && current.amount > 0 ? (
              <>
                <KeyValue
                  rows={[
                    { label: 'Commission', value: moneyExact(charge) },
                    { label: `GST (${current.taxPercent}%)`, value: moneyExact(tax) },
                    { label: 'Total charged', value: moneyExact(charge + tax), kind: 'total' },
                  ]}
                />
                <p className="muted" style={{ fontSize: '.82rem', marginTop: 12 }}>
                  {current.paidBy === 'merchant'
                    ? `Debited from the merchant wallet when the order settles. The shopper pays ${moneyExact(sample)}.`
                    : `Added to the order total — the shopper pays ${moneyExact(sample + charge + tax)}.`}
                </p>
              </>
            ) : (
              <p className="muted" style={{ fontSize: '.87rem' }}>
                {current.enabled
                  ? 'Set a rate above to see what an order would be charged.'
                  : 'Commission is off — nothing is charged.'}
              </p>
            )}
          </Card>

          <Card title="What this affects">
            <Ticks
              items={[
                'A plan with its own fee overrides the commission for that plan',
                'Charged on settled orders only; failed and cancelled orders cost nothing',
                'Frozen onto each order at creation, so past orders never change',
                'Refunding an order credits its commission back',
              ]}
            />
          </Card>
        </div>
      </div>

    </>
  )
}

// ── WhatsApp sender ─────────────────────────────────────────────────────────

interface WaCounts {
  sent: number
  failed: number
  skipped: number
  queued: number
  charged: number
}

interface WaEvent {
  key: string
  event: string
  label: string
  hint: string
  template: string
  price: number
  stats: WaCounts
}

interface WaTemplate {
  name: string
  language: string
  category: string
  status: string
  statusLabel: string
  sendable: boolean
  header: string | null
  body: string | null
  variables: number | null
  stats: WaCounts
}

interface WaOverview {
  days: number
  configured: boolean
  reachable: boolean
  error: string | null
  wallet: { balance: string; currency: string; isLow: boolean } | null
  templates: WaTemplate[]
  events: WaEvent[]
  totals: WaCounts
  shopOverrides: number
}

interface WaHistoryRow {
  id: string
  event: string
  eventLabel: string
  status: 'queued' | 'sent' | 'failed' | 'skipped'
  phone: string | null
  template: string | null
  amount: string
  error: string | null
  shopDomain: string | null
  at: string
  resendable: boolean
}

interface WaMessage {
  id: string
  template: string | null
  language: string
  found: boolean
  header: string | null
  body: string | null
  variables: string[]
  rendered: { header: string | null; body: string | null } | null
  reason: string | null
}

interface WaHistory {
  items: WaHistoryRow[]
  total: number
  page: number
  perPage: number
  hasMore: boolean
}

const WA_STATUS_TONE: Record<string, string> = {
  sent: 'ok',
  failed: 'bad',
  skipped: 'warn',
  queued: 'info',
}

/**
 * Wakit wallet, approved templates, what each one has sent and failed, and the
 * per-message price every connected store is charged. The price saved here is
 * the platform layer: stores read it through settings (the WooCommerce plugin
 * shows it from its settings payload) and the notifier debits exactly that
 * amount from the merchant's Outfalak wallet on each send.
 */
export function AdminWhatsAppPage(): React.ReactNode {
  const [days, setDays] = useState<'7' | '30' | '90'>('30')

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'whatsapp', days],
    queryFn: () => api<WaOverview>(`/v1/admin/whatsapp?days=${days}`),
  })

  return (
    <>
      <PageHead
        title="WhatsApp"
        subtitle="Wakit wallet, approved templates, delivery and what each message costs a merchant."
      />

      <Tabs
        value={days}
        onChange={setDays}
        tabs={[
          { key: '7', label: 'Last 7 days' },
          { key: '30', label: 'Last 30 days' },
          { key: '90', label: 'Last 90 days' },
        ]}
      />

      {isPending && <StatsSkeleton count={4} />}

      {error && (
        <Empty error title="Could not load WhatsApp">
          {(error as Error).message}
        </Empty>
      )}

      {data && (
        <>
          {data.error && (
            <Notice kind="warn" title="Wakit">
              {data.error}
            </Notice>
          )}

          <section className="grid grid--4">
            <Stat
              label="Wakit wallet"
              value={data.wallet ? data.wallet.balance || '—' : '—'}
              icon={<WalletIcon size={20} />}
              tone={data.wallet?.isLow ? 'var(--bad)' : 'var(--brand)'}
              footnote={
                data.wallet ? (
                  data.wallet.isLow ? (
                    <Badge tone="bad">Low balance</Badge>
                  ) : (
                    <span className="faint">{data.wallet.currency}</span>
                  )
                ) : (
                  <span className="faint">{data.configured ? 'Unavailable' : 'Not configured'}</span>
                )
              }
            />
            <Stat
              label="Sent"
              value={data.totals.sent.toLocaleString('en-IN')}
              icon={<CheckCircleIcon size={20} />}
              tone="var(--ok)"
              footnote={<span className="faint">Last {data.days} days</span>}
            />
            <Stat
              label="Failed"
              value={data.totals.failed.toLocaleString('en-IN')}
              icon={<AlertIcon size={20} />}
              tone={data.totals.failed > 0 ? 'var(--bad)' : 'var(--brand)'}
              footnote={<span className="faint">{data.totals.skipped.toLocaleString('en-IN')} skipped</span>}
            />
            <Stat
              label="Charged to merchants"
              value={moneyExact(data.totals.charged)}
              icon={<RupeeIcon size={20} />}
              tone="var(--info)"
              footnote={<span className="faint">Debited from their wallets</span>}
            />
          </section>

          <WhatsAppEventsCard events={data.events} templates={data.templates} shopOverrides={data.shopOverrides} />
          <WhatsAppTemplatesCard templates={data.templates} />
        </>
      )}

      <ReminderFunnelCard scope="platform" />

      <WhatsAppHistoryCard />
    </>
  )
}

function WhatsAppEventsCard({
  events,
  templates,
  shopOverrides,
}: {
  events: WaEvent[]
  templates: WaTemplate[]
  shopOverrides: number
}): React.ReactNode {
  const queryClient = useQueryClient()
  const [edits, setEdits] = useState<Record<string, { price: number; template: string }>>({})
  const [saved, setSaved] = useState(false)

  // The checkout_* templates are the ones this platform owns, so they are offered even while
  // WhatsApp is still reviewing them (marked below); anything else must already be approved.
  const sendable = templates.filter((row) => row.sendable || row.name.startsWith('checkout'))

  const rows = events.map((event) => ({
    ...event,
    price: edits[event.key]?.price ?? event.price,
    template: edits[event.key]?.template ?? event.template,
  }))

  const dirty = Object.keys(edits).length > 0

  const save = useMutation({
    mutationFn: () =>
      api<{ events: WaEvent[] }>('/v1/admin/whatsapp/events', {
        method: 'PUT',
        body: { events: rows.map((row) => ({ key: row.key, price: row.price, template: row.template })) },
      }),
    onSuccess: () => {
      setEdits({})
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] })
    },
  })

  function change(key: string, patch: Partial<{ price: number; template: string }>): void {
    const row = rows.find((entry) => entry.key === key)
    if (!row) return
    setEdits({ ...edits, [key]: { price: row.price, template: row.template, ...patch } })
  }

  return (
    <Card
      title="Message price and template"
      subtitle="Per message, in rupees. Every connected store is charged this from its Outfalak wallet."
      action={
        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {saved && (
        <Notice kind="ok" title="Saved">
          Applies to every store on its next message and shows in the plugin settings on its next sync.
        </Notice>
      )}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {shopOverrides > 0 && (
        <Notice kind="warn" title="Some stores override a price">
          {shopOverrides} store setting{shopOverrides === 1 ? '' : 's'} hold their own price for these events and
          ignore the value saved here.
        </Notice>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Template</th>
              <th style={{ width: '9rem' }}>Price / message (₹)</th>
              <th className="right">Sent</th>
              <th className="right">Failed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const known = sendable.some((template) => template.name === row.template)

              return (
                <tr key={row.key}>
                  <td>
                    <span>
                      {row.label}
                      <span className="table__sub">{row.hint}</span>
                    </span>
                  </td>
                  <td>
                    <select
                      className="input"
                      value={row.template}
                      onChange={(event) => change(row.key, { template: event.target.value })}
                    >
                      {!known && <option value={row.template}>{row.template} (not approved yet)</option>}
                      {sendable.map((template) => (
                        <option key={`${template.name}:${template.language}`} value={template.name}>
                          {template.name} · {template.language}
                          {template.sendable ? '' : ` (${template.statusLabel || template.status})`}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <DecimalInput value={row.price} onNumber={(next) => change(row.key, { price: next })} />
                  </td>
                  <td className="right num">{row.stats.sent.toLocaleString('en-IN')}</td>
                  <td className="right num">{row.stats.failed.toLocaleString('en-IN')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function WhatsAppTemplatesCard({ templates }: { templates: WaTemplate[] }): React.ReactNode {
  const [checkoutOnly, setCheckoutOnly] = useState(true)

  const rows = [...templates]
    .filter((row) => (checkoutOnly ? row.name.startsWith('checkout') : true))
    .sort((a, b) => b.stats.sent + b.stats.failed - (a.stats.sent + a.stats.failed) || a.name.localeCompare(b.name))

  return (
    <Card
      title="Templates"
      subtitle={`${templates.filter((row) => row.sendable).length} approved of ${templates.length} in the Wakit workspace.`}
      action={
        <Switch
          checked={checkoutOnly}
          onChange={setCheckoutOnly}
          label="Checkout templates only"
        />
      }
    >
      {rows.length === 0 ? (
        <Empty icon={<ShieldIcon size={24} />} title="No templates to show" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Category</th>
                <th>Status</th>
                <th className="right">Sent</th>
                <th className="right">Failed</th>
                <th className="right">Charged</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.name}:${row.language}`}>
                  <td>
                    <span>
                      {row.name}
                      <span className="table__sub">
                        {row.language}
                        {row.header ? ` · ${row.header}` : ''}
                      </span>
                    </span>
                  </td>
                  <td>{humanise(row.category) || '—'}</td>
                  <td>
                    <Badge tone={row.sendable ? 'ok' : row.status === 'rejected' ? 'bad' : 'warn'}>
                      {row.statusLabel || humanise(row.status)}
                    </Badge>
                  </td>
                  <td className="right num">{row.stats.sent.toLocaleString('en-IN')}</td>
                  <td className="right num">{row.stats.failed.toLocaleString('en-IN')}</td>
                  <td className="right num">{moneyExact(row.stats.charged)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function WhatsAppHistoryCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'all' | 'sent' | 'failed' | 'skipped' | 'queued'>('all')
  const [page, setPage] = useState(1)
  const [viewing, setViewing] = useState<WaHistoryRow | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'whatsapp', 'history', status, page],
    queryFn: () =>
      api<WaHistory>('/v1/admin/whatsapp/history', {
        query: { status: status === 'all' ? undefined : status, page, perPage: PER_PAGE },
      }),
  })

  const resend = useMutation({
    mutationFn: (id: string) => api<{ queued: boolean }>(`/v1/admin/whatsapp/history/${id}/resend`, { method: 'POST' }),
    onSuccess: () => {
      // The worker takes a moment; refetch once now (queued) and again shortly after (sent or failed).
      void queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] })
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] }), 4000)
    },
  })

  return (
    <Card title="History" subtitle="Every send attempt, newest first — with the reason when it did not go.">
      {resend.error && (
        <Notice kind="error" title="Could not resend">
          {(resend.error as Error).message}
        </Notice>
      )}

      {resend.isSuccess && (
        <Notice kind="ok" title="Queued again">
          The message is back on the queue. The merchant is charged only if Wakit accepts it.
        </Notice>
      )}

      <Tabs
        value={status}
        onChange={(key) => {
          setStatus(key)
          setPage(1)
        }}
        tabs={[
          { key: 'all', label: 'All' },
          { key: 'sent', label: 'Sent' },
          { key: 'failed', label: 'Failed' },
          { key: 'skipped', label: 'Skipped' },
          { key: 'queued', label: 'Queued' },
        ]}
      />

      {error && (
        <Empty error title="Could not load history">
          {(error as Error).message}
        </Empty>
      )}

      {isPending && <RowsSkeleton rows={6} />}

      {data && data.items.length === 0 && !error && (
        <Empty icon={<CheckCircleIcon size={24} />} title="Nothing here yet" />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Store</th>
                  <th>Event</th>
                  <th>Template</th>
                  <th>To</th>
                  <th>Status</th>
                  <th className="right">Charged</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id}>
                    <td>{ago(row.at)}</td>
                    <td>{row.shopDomain ?? '—'}</td>
                    <td>{row.eventLabel}</td>
                    <td>{row.template ?? '—'}</td>
                    <td>{row.phone ?? '—'}</td>
                    <td>
                      <span>
                        <Badge tone={WA_STATUS_TONE[row.status] ?? undefined}>{humanise(row.status)}</Badge>
                        {row.error && <span className="table__sub">{row.error}</span>}
                      </span>
                    </td>
                    <td className="right num">{Number(row.amount) > 0 ? moneyExact(row.amount) : '—'}</td>
                    <td className="right">
                      <div className="row" style={{ justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'nowrap' }}>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setViewing(row)}>
                          View
                        </button>
                        {row.resendable && (
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={resend.isPending}
                            onClick={() => resend.mutate(row.id)}
                          >
                            {resend.isPending && resend.variables === row.id ? 'Resending…' : 'Resend'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            page={page}
            pageCount={Math.max(1, Math.ceil(data.total / PER_PAGE))}
            total={data.total}
            perPage={PER_PAGE}
            onPage={setPage}
            noun="messages"
          />
        </>
      )}

      {viewing && (
        <WhatsAppMessageDrawer
          row={viewing}
          onClose={() => setViewing(null)}
          onResend={
            viewing.resendable
              ? () => {
                  resend.mutate(viewing.id)
                  setViewing(null)
                }
              : undefined
          }
        />
      )}
    </Card>
  )
}

/** The template as Wakit holds it, the values this message puts in it, and the two together. */
function WhatsAppMessageDrawer({
  row,
  onClose,
  onResend,
}: {
  row: WaHistoryRow
  onClose: () => void
  onResend?: () => void
}): React.ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'whatsapp', 'message', row.id],
    queryFn: () => api<WaMessage>(`/v1/admin/whatsapp/history/${row.id}`),
  })

  const preStyle: React.CSSProperties = { whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }

  return (
    <Drawer
      open
      onClose={onClose}
      title={row.eventLabel}
      subtitle={`${row.shopDomain ?? 'No store'} · ${dateTime(row.at)}`}
      footer={
        onResend && (
          <button type="button" className="btn btn--primary" onClick={onResend}>
            Resend
          </button>
        )
      }
    >
      <DrawerSection title="Delivery">
        <KeyValue
          rows={[
            { label: 'Status', value: <Badge tone={WA_STATUS_TONE[row.status] ?? undefined}>{humanise(row.status)}</Badge> },
            ...(row.error ? [{ label: 'Error', value: row.error }] : []),
            { label: 'To', value: row.phone ?? '—' },
            { label: 'Template', value: data ? `${data.template ?? '—'} · ${data.language}` : (row.template ?? '—') },
            { label: 'Charged', value: Number(row.amount) > 0 ? moneyExact(row.amount) : '—' },
          ]}
        />
      </DrawerSection>

      {isPending && <RowsSkeleton rows={4} />}

      {error && (
        <Empty error title="Could not load the message">
          {(error as Error).message}
        </Empty>
      )}

      {data && (
        <>
          {data.reason && (
            <Notice kind="warn" title="Values unavailable">
              {data.reason}
            </Notice>
          )}

          {!data.found && data.template && (
            <Notice kind="warn" title="Template not in Wakit">
              “{data.template}” is not in the Wakit workspace, so its text cannot be shown.
            </Notice>
          )}

          {data.rendered && (data.rendered.header || data.rendered.body) && (
            <DrawerSection title="Message">
              {data.rendered.header && (
                <p style={{ ...preStyle, fontWeight: 600, marginBottom: '0.5rem' }}>{data.rendered.header}</p>
              )}
              {data.rendered.body && <p style={preStyle}>{data.rendered.body}</p>}
            </DrawerSection>
          )}

          {data.variables.length > 0 && (
            <DrawerSection title="Values">
              <KeyValue rows={data.variables.map((value, index) => ({ label: `{{${index + 1}}}`, value: value || '—' }))} />
            </DrawerSection>
          )}

          {(data.header || data.body) && (
            <DrawerSection title="Template">
              {data.header && (
                <p className="faint" style={{ ...preStyle, fontWeight: 600, marginBottom: '0.5rem' }}>
                  {data.header}
                </p>
              )}
              {data.body && (
                <p className="faint" style={preStyle}>
                  {data.body}
                </p>
              )}
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  )
}
